'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { motion } from 'framer-motion';
import BookingProgressIndicator from '@/components/common/BookingProgressIndicator';
import ServiceSelectionStep from './ServiceSelectionStep';
import SmartMeasurementStep from './SmartMeasurementStep';
import ServiceMeasurementStep from './ServiceMeasurementStep';
import SofaSeatSelectionStep from './SofaSeatSelectionStep';
import BedSelectionStep from './BedSelectionStep';
import MeasurementVerificationStep from './MeasurementVerificationStep';
import ScheduleSelectionStep from './ScheduleSelectionStep';
import AddressDetailsStep from './AddressDetailsStep';
import PaymentStep from './PaymentStep';
import PricingSidebar, { PriceBreakdownCard } from './PricingSidebar';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import Icon from '@/components/ui/AppIcon';
import CarpetMultiSizeEditor from './CarpetMultiSizeEditor';
import ServiceConfigSheet from './ServiceConfigSheet';
import { cleanServiceTitle } from '@/lib/serviceDisplay';
import { PAY_IN_FULL_DISCOUNT_RATE } from '@/lib/constants';
import AppImage from '@/components/ui/AppImage';
import {
  carpetLineCost,
  carpetTotalCount,
  carpetDescription,
  carpetLineItems,
  normalizeCarpetItems,
} from '@/lib/carpetMeasurements';
import {
  curtainLineCost,
  curtainTotalPanels,
  curtainDescription,
  curtainLineItems,
  normalizeCurtainItems,
} from '@/lib/curtainMeasurements';
import { newEventId, trackStandardEvent } from '@/lib/metaPixel';
import { measureOpenAiEvent } from '@/lib/openaiPixel';

interface FurnitureItem {
  id: string;
  name: string;
  description: string;
  pricePerUnit: number;
  unit: string;
  icon: string;
  quantity: number;
  category?: string;
}

// Module-level conversion constant — shared by the parent's pricing math
// (inside calculatePricing) and by the multi-service Extra cards below.
const SQM_TO_SQFT = 10.764;

// Upholstery services all share one quantity stepper. Most (bed nanocoating,
// loose cushions) top out around 5 for the quick-select shortcuts, but a
// dining/office chair SET commonly runs to 6–8 pieces — so chair services get
// the extended shortcut row and are counted as "chairs" rather than the
// generic "bed"/"item" label. Used by BOTH the primary stepper (BedSelectionStep)
// and the extra-service stepper (ExtraStepperCard) so the two never diverge.
const UPHOLSTERY_QUICK_DEFAULT = [1, 2, 3, 4, 5];
const UPHOLSTERY_QUICK_CHAIR = [1, 2, 3, 4, 5, 6, 7, 8];
const isChairService = (name?: string): boolean =>
  (name || '').toLowerCase().includes('chair');

// ── Per-line measurement helpers ────────────────────────────────────────
// One implementation for the primary service AND every extra. These back
// the config sheet's CTA gate, the summary strips, and the Continue gate,
// so all three can never disagree about whether a line is "set up" or what
// it costs. Previously the primary and the extras each had their own copy.
const measurementValidFor = (categoryType: string, m: any): boolean => {
  if (!m) return false;
  switch (categoryType) {
    case 'sofa':
      return !!(m.numberOfSeats && m.numberOfSeats > 0);
    case 'curtain':
      // Multi-size: valid as long as at least one row has positive
      // width/length/panels. Normalizer also accepts the legacy
      // single-size shape for back-compat.
      return normalizeCurtainItems(m).length > 0;
    case 'carpet':
      return normalizeCarpetItems(m).length > 0;
    case 'upholstery':
      return !!(m.quantity && m.quantity > 0);
    default:
      return false;
  }
};

const lineTotalFor = (categoryType: string, m: any, basePrice: number): number => {
  if (!m || !basePrice) return 0;
  switch (categoryType) {
    case 'sofa':
      return (m.numberOfSeats || 0) * basePrice;
    case 'upholstery':
      return (m.quantity || 0) * basePrice;
    case 'curtain':
      return Number(curtainLineCost(m, basePrice) || 0);
    case 'carpet':
      return Number(carpetLineCost(m, basePrice) || 0);
    default:
      return 0;
  }
};

const qtySummaryFor = (categoryType: string, m: any): string => {
  if (!m) return '';
  switch (categoryType) {
    case 'sofa': {
      const n = m.numberOfSeats || 0;
      return `${n} ${n === 1 ? 'seat' : 'seats'}`;
    }
    case 'curtain': {
      const n = curtainTotalPanels(m);
      return `${n} ${n === 1 ? 'panel' : 'panels'}`;
    }
    case 'carpet': {
      const n = carpetTotalCount(m);
      return `${n} ${n === 1 ? 'carpet' : 'carpets'}`;
    }
    case 'upholstery': {
      const n = m.quantity || 0;
      return `${n} ${n === 1 ? 'piece' : 'pieces'}`;
    }
    default:
      return '';
  }
};

// Sheet subtitle + disabled-CTA hint, shared by primary and extras so the
// two paths read identically.
const sheetSubtitleFor = (
  categoryType: string,
  details: { basePrice: number; unit: string } | null
): string =>
  details
    ? `₹${details.basePrice.toLocaleString('en-IN')} per ${details.unit}${
        categoryType === 'sofa' ? ' · 1 seat = up to 2 ft wide' : ''
      }`
    : '';

const measurementHintFor = (categoryType: string): string =>
  categoryType === 'sofa'
    ? 'Add at least 1 seat to continue'
    : categoryType === 'upholstery'
      ? 'Add at least 1 piece to continue'
      : 'Enter at least one measurement to continue';

// Map a service NAME to the AI vision prompt key so the model counts the
// right item. Upholstery covers several distinct items (cushions, ottomans,
// chairs) that need their own prompt/`count`; sofa/carpet/curtain already
// match their categoryType, so they return undefined and fall back to it.
// Keep keys in sync with the prompts table in /api/ai-measure/route.ts.
const resolveAiItemType = (serviceName: string): string | undefined => {
  const n = (serviceName || '').toLowerCase();
  if (n.includes('cushion') || n.includes('pillow')) return 'cushion';
  if (n.includes('ottoman') || n.includes('pouf') || n.includes('footstool')) return 'ottoman';
  if (n.includes('chair')) return 'chair';
  return undefined;
};

const BookingFlowInteractive = () => {
  const router = useRouter();
  const { user } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState<
    Array<string | { id: string; name: string; price: number }>
  >([]);
  const [furnitureItems, setFurnitureItems] = useState<FurnitureItem[]>([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState<
    'sofa' | 'curtain' | 'carpet' | 'upholstery' | ''
  >('');
  const [smartMeasurements, setSmartMeasurements] = useState<Record<string, any>>({});
  const [serviceMeasurements, setServiceMeasurements] = useState<{
    numberOfSeats?: number;
    seatMeasurements?: Array<{ seatNumber: number; width: number }>;
    // Curtain — multi-size: each entry is N panels of a specific size.
    // curtainWidth / curtainLength / numberOfPanels remain readable for
    // back-compat with legacy bookings.
    curtainItems?: Array<{
      label?: string;
      width: number;
      length: number;
      quantity: number;
    }>;
    curtainWidth?: number;
    curtainLength?: number;
    numberOfPanels?: number;
    // Carpet — new multi-size shape. Each entry is one (or N identical)
    // carpets of a specific width × length. carpetWidth / carpetLength /
    // numberOfCarpets remain for back-compat reads of legacy bookings only.
    carpetItems?: Array<{
      label?: string;
      width: number;
      length: number;
      quantity: number;
    }>;
    carpetWidth?: number;
    carpetLength?: number;
    numberOfCarpets?: number;
    quantity?: number;
    measurementUnit?: 'feet' | 'meters';
    images?: string[];
  }>({});
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [aiVerificationData, setAiVerificationData] = useState<any>(null);
  // Measurement capture mode for the PRIMARY service. The AI photo-check
  // used to be a separate step (old Step 3); it now lives inline in Step 1
  // as an opt-in alternative to plain manual entry. 'manual' = type the
  // numbers and continue; 'ai' = type the numbers AND upload a photo so the
  // AI confirms/adjusts them (mandatory verification before Continue).
  // Defaults to 'manual' so the lowest-friction path is the default and the
  // AI round-trip never blocks a customer who didn't opt in.
  const [primaryMeasurementMode, setPrimaryMeasurementMode] = useState<'manual' | 'ai'>('manual');
  // Multi-service AI verification — each service the customer picked on
  // Step 1 has its own verification status + AI blob. 'primary' is the key
  // for the top-level service; every extra uses its own uid. Step 3 is
  // valid only when every service in this map is true.
  const [verifiedMap, setVerifiedMap] = useState<Record<string, boolean>>({});
  const [aiVerificationMap, setAiVerificationMap] = useState<Record<string, any>>({});
  // System-wide surge toggle — fetched from /api/settings/public on mount
  // so the sidebar's local surge calc + calendar highlighting mirror what
  // the server actually charges. Defaults to `true` (legacy behaviour) so
  // the very first render before the fetch resolves matches the server's
  // default. The server still re-checks the toggle on every booking
  // request, so a tampered client can't bypass the policy.
  const [surgeEnabled, setSurgeEnabled] = useState<boolean>(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/settings/public');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && typeof data.surgeEnabled === 'boolean') {
          setSurgeEnabled(data.surgeEnabled);
        }
      } catch {
        /* fail open — keep the default true */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  // Bridge to PaymentStep's charge action, so the pricing sidebar (desktop)
  // and the mobile bottom bar can trigger the SAME payment as the in-page
  // "Pay" button. PaymentStep registers its handler here; the CTAs call
  // triggerPayment() on the payment step (6).
  const payHandlerRef = useRef<(() => void) | null>(null);
  const registerPayHandler = useCallback((fn: (() => void) | null) => {
    payHandlerRef.current = fn;
  }, []);
  const triggerPayment = () => {
    if (isPaymentProcessing) return;
    payHandlerRef.current?.();
  };
  // Booking is created BEFORE the customer is charged so a Razorpay failure
  // can never strand a paid customer without a booking record. The draft
  // sits in paymentStatus='pending' until the payment record links to it.
  // Cached here so retrying Pay (e.g. after closing Razorpay) reuses the
  // same draft instead of duplicating it.
  const [createdBookingDocId, setCreatedBookingDocId] = useState<string | null>(null);
  // Human-readable bookingId (e.g. WC-2-6-2-7-65946) captured at draft
  // creation. Used by the post-payment redirect to build the
  // /booking-confirmation?bookingId=… URL for non-customer sessions (admins
  // testing the flow) that have no guest token and no customer dashboard.
  const [createdBookingId, setCreatedBookingId] = useState<string>('');
  // Signature tracks the inputs that determine pricing — if any change
  // between Pay clicks, the cached draft is stale and the next Pay POSTs
  // a fresh one (otherwise close-Razorpay → change coupon → Pay would
  // reuse a draft priced at the old coupon).
  const [createdBookingSignature, setCreatedBookingSignature] = useState<string>('');
  // Returned by /api/bookings only for guest (not-logged-in) bookings. After
  // payment a guest is sent to /booking-confirmation?token=… — their only way
  // back to the booking, since they have no dashboard.
  const [guestAccessToken, setGuestAccessToken] = useState<string>('');
  // Mirror of the guest token in a ref. handlePaymentSuccess runs from the
  // Razorpay success callback, whose closure can capture a stale `guestAccessToken`
  // state value (the draft is created and the token set in the SAME click, before
  // a re-render). The ref is always current, so the post-payment redirect reliably
  // sends guests to the token-guarded confirmation page instead of ?bookingId=.
  const guestAccessTokenRef = useRef<string>('');

  // Abandoned-checkout capture — set when the customer first reaches the
  // payment step. If they leave without paying, this lead persists for the
  // admin Remarket tab. If payment succeeds, the lead is marked converted.
  const [abandonedLeadId, setAbandonedLeadId] = useState<string | null>(null);

  // Customer's chosen payment plan — lifted up from PaymentStep so the
  // pricing memo can react (full = 10% off services + travel fee waived;
  // advance = travel fee waived only; cod = neither).
  const [paymentPlan, setPaymentPlan] = useState<'full' | 'advance' | 'cod'>('full');
  const [serviceDetails, setServiceDetails] = useState<{
    name: string;
    basePrice: number;
    unit: string;
  } | null>(null);

  // Urban Company-style configurator sheet (Step 1). Opens on service-card
  // tap; "Add to estimate" closes it. Presentation only — the measurement
  // state it edits is the same serviceMeasurements the page always used.
  //
  // ONE sheet serves EVERY service. `sheetTarget` says which line it is
  // currently editing: the primary, or a specific extra by uid. Extras used
  // to configure in a card ~1,500px down the page while the primary got the
  // sheet — same job, two completely different UIs, and the second tap felt
  // like nothing happened. Null = closed.
  type SheetTarget = { kind: 'primary' } | { kind: 'extra'; uid: string } | null;
  const [sheetTarget, setSheetTarget] = useState<SheetTarget>(null);

  // Multi-service: zero or more ADDITIONAL services stacked onto this same
  // booking — each carries its own service ID, category type, measurements
  // and (lazy-loaded) service-details snapshot. Mirrors the admin manual
  // booking's `extraServices` payload and converges into the same
  // server-side `extraServices` array on submit.
  interface ExtraServiceEntry {
    uid: string;
    serviceId: string; // category id (selectedService analogue)
    packageId: string; // actual service ObjectId (selectedPackage analogue)
    categoryType: 'sofa' | 'curtain' | 'carpet' | 'upholstery' | '';
    serviceMeasurements: typeof serviceMeasurements;
    details: { name: string; basePrice: number; unit: string } | null;
    // Per-extra measurement mode — mirrors primaryMeasurementMode. 'ai'
    // requires the extra's photo to be AI-verified before Continue.
    measurementMode: 'manual' | 'ai';
  }
  const [extraServiceEntries, setExtraServiceEntries] = useState<ExtraServiceEntry[]>([]);

  const addExtraService = () => {
    const uid = `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setExtraServiceEntries((prev) => [
      ...prev,
      {
        uid,
        serviceId: '',
        packageId: '',
        categoryType: '',
        serviceMeasurements: {},
        details: null,
        measurementMode: 'manual',
      },
    ]);
    // Blank entry — the sheet opens on its service picker. Closing without
    // picking discards it (see closeSheet), so this can't leave an empty
    // line behind blocking Continue.
    setSheetTarget({ kind: 'extra', uid });
  };

  // Full customer-facing catalogue, lifted from ServiceSelectionStep once its
  // fetch resolves (API or fallback). Powers the cross-sell strip from the
  // SAME list that renders the Step 1 cards — no duplicate request, no drift.
  const [catalog, setCatalog] = useState<
    Array<{
      _id: string;
      name: string;
      slug?: string;
      type?: 'sofa' | 'curtain' | 'carpet' | 'upholstery';
      image?: string;
      icon?: string;
      startingPrice?: string;
      basePrice?: number;
      unit?: string;
    }>
  >([]);

  // Cross-sell add — same shape addExtraService creates, but pre-seeded with
  // the tapped catalogue item so the extra's card skips the service picker
  // and opens straight on its measurement UI. When the card carries a
  // numeric basePrice the details seed makes the configurator render
  // immediately; otherwise the per-extra fetch effect above fills them in.
  const addCrossSellService = (item: (typeof catalog)[number]) => {
    const uid = `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setExtraServiceEntries((prev) => [
      ...prev,
      {
        uid,
        serviceId: item._id,
        packageId: item._id,
        categoryType: (item.type || '') as 'sofa' | 'curtain' | 'carpet' | 'upholstery' | '',
        serviceMeasurements: {},
        details:
          typeof item.basePrice === 'number' && item.basePrice > 0
            ? {
                name: item.name,
                basePrice: item.basePrice,
                unit:
                  item.unit ||
                  (item.type === 'sofa' ? 'seat' : item.type === 'upholstery' ? 'Pc' : 'sq.ft'),
              }
            : null,
        measurementMode: 'manual',
      },
    ]);
    // Cross-sell attach signal — content_category distinguishes it from the
    // primary AddToCart fired by the config sheet.
    trackStandardEvent('AddToCart', {
      content_name: item.name,
      content_category: 'cross-sell',
      currency: 'INR',
    });
    // Open the configurator ON the tap, same as the primary service. This
    // used to scroll to a card far down the page instead — the tap and its
    // next action ended up in different viewports.
    setSheetTarget({ kind: 'extra', uid });
  };

  // Items for the cross-sell strip: everything in the catalogue except the
  // primary selection and extras already added. Capped at 6 so the strip
  // stays a strip — the full list remains reachable via "Browse all".
  const crossSellItems = catalog
    .filter(
      (c) =>
        c._id !== selectedService &&
        c._id !== selectedPackage &&
        !extraServiceEntries.some((e) => e.serviceId === c._id || e.packageId === c._id)
    )
    .slice(0, 6);

  const removeExtraService = (uid: string) => {
    setExtraServiceEntries((prev) => prev.filter((e) => e.uid !== uid));
  };

  const updateExtraService = (uid: string, patch: Partial<ExtraServiceEntry>) => {
    setExtraServiceEntries((prev) => prev.map((e) => (e.uid === uid ? { ...e, ...patch } : e)));
  };

  // Fetch per-extra service details whenever its packageId changes — same
  // pattern as the primary serviceDetails fetch below, but per extra entry.
  // categoryType lives on Service.type now (moved off ServiceCategory in
  // a7566d6), so we derive it from data.type — no separate category picker
  // needed on the card itself.
  useEffect(() => {
    const pending = extraServiceEntries.filter(
      (e) => e.packageId && (!e.details || e.details.name === '')
    );
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const entry of pending) {
        try {
          const res = await fetch(`/api/services/${entry.packageId}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (cancelled) return;
          const derivedType = (data?.type as 'sofa' | 'curtain' | 'carpet' | 'upholstery') || '';
          // Seed a sensible default quantity so the price card and the line
          // total agree from the moment the service is picked (matches the
          // admin form's `seed quantity=1 for synthetic stepper` rule).
          const seededMeasurements: any = { ...entry.serviceMeasurements };
          if (derivedType === 'sofa' && !seededMeasurements.numberOfSeats) {
            seededMeasurements.numberOfSeats = 1;
          } else if (derivedType === 'upholstery' && !seededMeasurements.quantity) {
            seededMeasurements.quantity = 1;
          }
          updateExtraService(entry.uid, {
            details: {
              name: data.name || '',
              basePrice: data.basePrice || 0,
              unit: data.unit || '',
            },
            categoryType: derivedType,
            serviceMeasurements: seededMeasurements,
          });
        } catch {
          /* leave as null; pricing falls back to 0 for this entry */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraServiceEntries.map((e) => `${e.uid}:${e.packageId}`).join('|')]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [addressData, setAddressData] = useState({
    fullName: '',
    phone: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    pincode: '',
    landmark: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    locationSaved: false,
    // Per-pincode transport charge, populated by AddressDetailsStep from the
    // /api/serviceable-areas check. The booking POST recomputes pricing on
    // the server using the same ServiceableArea record, so keeping this in
    // sync prevents a paid-vs-billed mismatch (was hardcoded ₹299 here).
    transportCharge: undefined as number | undefined,
  });

  const addressSchema = z.object({
    fullName: z.string().trim().min(1).min(3),
    phone: z
      .string()
      .trim()
      .min(1)
      .refine((val) => /^\d{10}$/.test(val.replace(/\D/g, ''))),
    email: z.string().trim().min(1).email(),
    addressLine1: z.string().trim().min(1),
    addressLine2: z.string().trim().max(200).optional().or(z.literal('')),
    city: z.string().trim().min(1),
    pincode: z
      .string()
      .trim()
      .min(1)
      .refine((val) => /^\d{6}$/.test(val)),
    landmark: z.string().trim().max(200).optional().or(z.literal('')),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    locationSaved: z.boolean().optional(),
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Fetch service details when package is selected. Confirms/overwrites the
  // optimistic seed set by the card-tap handler with server truth.
  //
  // Deliberately NO else-branch clearing serviceDetails to null: during a
  // category switch, ServiceSelectionStep momentarily fires
  // onPackageChange('') before its auto-select re-fires with the new
  // package id. Clearing here would wipe the instant seed and flash
  // "Loading service…" in the config sheet for a full network round trip
  // (or forever in local dev without a DB). Stale details are impossible in
  // practice — every card tap re-seeds synchronously, and this fetch then
  // overwrites with the server's values.
  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!selectedPackage) return;
      try {
        const res = await fetch(`/api/services/${selectedPackage}`);
        if (res.ok) {
          const data = await res.json();
          setServiceDetails({
            name: data.name || '',
            basePrice: data.basePrice || 0,
            unit: data.unit || '',
          });
        }
      } catch (error) {
        console.error('Error fetching service details:', error);
      }
    };
    fetchServiceDetails();
  }, [selectedPackage]);

  // ------------------------------------------------------------------
  // captureAbandonedCheckout — fires when the customer first lands on the
  // payment step. Writes a Lead (leadSource: 'abandoned_checkout') with
  // everything they filled in so the admin Remarket tab can recover the
  // booking if payment never completes. On payment success the lead is
  // marked 'converted' (see handlePaymentSuccess).
  //
  // MUST be defined above the `if (!isHydrated) return …` early-return
  // below so hooks run in the same order on every render. The effect body
  // uses closures (calculatePricing / buildBookingPayload helpers defined
  // further down) which resolve at call time, not at definition time.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (currentStep !== 6) return;
    const fullName = addressData.fullName || user?.name || '';
    const email = addressData.email || user?.email || '';
    const phone = addressData.phone || user?.phone || '';
    if (!email && !phone) return; // need at least one contact channel

    const pricingNow = calculatePricing();

    // An abandoned checkout has paid nothing, so it has earned neither prepaid
    // incentive. The payment step pre-selects the "full" plan, so pricingNow
    // already carries the 10% services discount and a waived travel fee —
    // snapshotting that quoted admins a price the customer never paid for, and
    // the lead converter went on to create the booking at it. Capture the
    // neutral unpaid quote; the incentive is re-applied at conversion only if
    // an admin sets an explicit prepaid preference on the lead.
    const unpaidTravelFee = (pricingNow.travelFee || 0) + (pricingNow.travelWaivedAmount || 0);
    const unpaidTotal =
      (pricingNow.servicesTotal || 0) + unpaidTravelFee + (pricingNow.surgeFee || 0);

    const payload = {
      customerId: user?.id && user?.role === 'customer' ? user.id : undefined,
      customer: { fullName, email, phone },
      serviceId: selectedPackage || undefined,
      serviceName: serviceDetails?.name || '',
      quantity:
        serviceMeasurements?.numberOfSeats ||
        curtainTotalPanels(serviceMeasurements) ||
        carpetTotalCount(serviceMeasurements) ||
        serviceMeasurements?.quantity ||
        1,
      furnitureItems: furnitureItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        pricePerUnit: item.pricePerUnit,
      })),
      addOns: selectedAddOns
        .filter(
          (ao: any) =>
            typeof ao === 'object' &&
            typeof ao?.id === 'string' &&
            ao.id.startsWith(`${selectedPackage}-`)
        )
        .map((ao: any) => ({
          name: ao.name,
          price: ao.price,
          quantity: 1,
        })),
      extraServices: extraServiceEntries
        .filter((e) => e.packageId)
        .map((e) => {
          // Pricing line for this extra (cost + description) is precomputed
          // in calculatePricing → extraServiceLines, keyed by uid.
          const line = (pricingNow.extraServiceLines || []).find((l: any) => l.uid === e.uid);
          const m = e.serviceMeasurements || {};
          let qty = 1;
          if (e.categoryType === 'sofa' && m.numberOfSeats) qty = m.numberOfSeats;
          else if (e.categoryType === 'upholstery' && m.quantity) qty = m.quantity;
          else if (e.categoryType === 'curtain') {
            const p = curtainTotalPanels(m);
            if (p > 0) qty = p;
          } else if (e.categoryType === 'carpet') {
            const c = carpetTotalCount(m);
            if (c > 0) qty = c;
          }
          return {
            serviceId: e.packageId,
            serviceName: e.details?.name || '',
            quantity: qty,
            lineTotal: line?.cost || 0,
            addOns: selectedAddOns
              .filter(
                (ao: any) =>
                  typeof ao === 'object' &&
                  typeof ao?.id === 'string' &&
                  ao.id.startsWith(`${e.packageId}-`)
              )
              .map((ao: any) => ({
                name: ao.name,
                price: ao.price,
                quantity: 1,
              })),
          };
        }),
      scheduledDate: selectedDate,
      scheduledSlot: {
        startTime: selectedSlot.split('-')[0]?.trim() || '',
        endTime: selectedSlot.split('-')[1]?.trim() || '',
      },
      address: {
        addressLine1: addressData.addressLine1,
        addressLine2: addressData.addressLine2,
        city: addressData.city,
        pincode: addressData.pincode,
        landmark: addressData.landmark,
      },
      pricingPreview: {
        primaryItemsTotal: pricingNow.basePrice || 0,
        addOnsTotal: Math.max(
          0,
          (pricingNow.servicesTotal || 0) -
            (pricingNow.basePrice || 0) -
            (pricingNow.extrasTotal || 0)
        ),
        extrasTotal: pricingNow.extrasTotal || 0,
        payInFullDiscount: 0,
        travelFee: unpaidTravelFee,
        surgeFee: pricingNow.surgeFee || 0,
        discount: 0,
        subtotal: unpaidTotal,
        total: unpaidTotal,
      },
    };

    let cancelled = false;
    fetch('/api/leads/abandoned-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.leadId) setAbandonedLeadId(data.leadId);
      })
      .catch(() => {
        /* best-effort — never block the UI */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:708:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="708" data-component-end-line="722" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:709:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="709" data-component-end-line="709" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-20%20bg-card%20border-b%20border-border%22%7D" className="h-20 bg-card border-b border-border" />
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:710:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="710" data-component-end-line="721" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20py-8%22%7D" className="max-w-7xl mx-auto px-4 py-8">
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:711:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="711" data-component-end-line="720" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:712:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="712" data-component-end-line="712" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded-md%20w-1%2F3%22%7D" className="h-12 bg-muted rounded-md w-1/3" />
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:713:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="713" data-component-end-line="719" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:714:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="714" data-component-end-line="717" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-6%22%7D" className="lg:col-span-2 space-y-6">
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:715:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="715" data-component-end-line="715" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-md%22%7D" className="h-64 bg-muted rounded-md" />
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:716:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="716" data-component-end-line="716" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-md%22%7D" className="h-64 bg-muted rounded-md" />
              </div>
              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:718:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="718" data-component-end-line="718" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-md%22%7D" className="h-96 bg-muted rounded-md" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add-ons will be dynamically fetched from service data
  const addOnPrices: Record<string, { name: string; price: number }> = {};

  const handleFurnitureQuantityChange = (
    itemId: string,
    quantity: number,
    itemData?: Omit<FurnitureItem, 'quantity'>
  ) => {
    setFurnitureItems((prev) => {
      const existingItem = prev.find((item) => item.id === itemId);

      if (quantity === 0) {
        return prev.filter((item) => item.id !== itemId);
      }

      if (existingItem) {
        return prev.map((item) => (item.id === itemId ? { ...item, quantity } : item));
      }

      // If item doesn't exist and we have itemData, create it
      if (itemData) {
        return [...prev, { ...itemData, quantity }];
      }

      // Fallback: try to find the item in existing items with similar ID pattern
      // This handles cases where itemData might not be passed
      return prev;
    });
  };

  const calculatePricing = () => {
    // Rates (basePrice) are stored per sq.ft. If user enters dimensions in
    // meters, convert sq.m → sq.ft (1 sq.m = 10.764 sq.ft) before pricing.
    // SQM_TO_SQFT is hoisted to module scope so the multi-service Extra
    // cards below can reuse the same constant.
    const isMeters = serviceMeasurements.measurementUnit === 'meters';
    const toSqFt = (area: number) => (isMeters ? area * SQM_TO_SQFT : area);

    // Calculate service cost based on measurements
    let serviceCost = 0;
    if (serviceDetails && selectedCategoryType) {
      switch (selectedCategoryType) {
        case 'sofa':
          if (serviceMeasurements.numberOfSeats && serviceMeasurements.numberOfSeats > 0) {
            serviceCost = serviceMeasurements.numberOfSeats * serviceDetails.basePrice;
          }
          break;
        case 'curtain': {
          // Multi-size curtain: each row in curtainItems[] contributes
          // (width × length × panels). Falls back to the legacy
          // single-size fields when curtainItems is absent.
          const cost = curtainLineCost(serviceMeasurements, serviceDetails.basePrice);
          if (cost != null) serviceCost = cost;
          break;
        }
        case 'carpet': {
          // Multi-size: each row in `carpetItems` contributes its own
          // (width × length × quantity) area. Falls back to the legacy
          // single-size fields when carpetItems is absent (back-compat).
          const cost = carpetLineCost(serviceMeasurements, serviceDetails.basePrice);
          if (cost != null) serviceCost = cost;
          break;
        }
        case 'upholstery':
          if (serviceMeasurements.quantity && serviceMeasurements.quantity > 0) {
            serviceCost = serviceMeasurements.quantity * serviceDetails.basePrice;
          }
          break;
      }
    }

    // Fallback to furniture items if no measurements yet
    const furnitureTotal = furnitureItems.reduce((sum, item) => {
      return sum + item.pricePerUnit * item.quantity;
    }, 0);

    const basePrice = serviceCost > 0 ? serviceCost : furnitureTotal;

    // One-line measurement summary for the base service. Quantity-style for
    // sofa/upholstery; dimension-style for curtain/carpet. Empty string when
    // the customer hasn't entered measurements yet (sidebar omits the line).
    const fmt = (n: number) => n.toLocaleString('en-IN');
    const unitLabel = serviceMeasurements.measurementUnit === 'meters' ? 'm' : 'ft';
    let baseServiceDescription = '';
    // Multi-size carpet/curtain → per-size auditable rows for the sidebar.
    // Only populated when there's more than one row; single-size keeps the
    // one-line description (already self-explanatory).
    let baseServiceItems:
      | Array<{
          label?: string;
          width: number;
          length: number;
          quantity: number;
          unitLabel: string;
          areaSqFt: number;
          amount: number;
        }>
      | undefined;
    let baseServiceRate: number | undefined;
    if (serviceDetails && selectedCategoryType) {
      switch (selectedCategoryType) {
        case 'sofa': {
          const seats = serviceMeasurements.numberOfSeats || 0;
          if (seats > 0) {
            baseServiceDescription = `${seats} seat${seats !== 1 ? 's' : ''} × ₹${fmt(serviceDetails.basePrice)}`;
          }
          break;
        }
        case 'upholstery': {
          const qty = serviceMeasurements.quantity || 0;
          if (qty > 0) {
            const unit = serviceDetails.unit || 'item';
            baseServiceDescription = `${qty} ${unit}${qty !== 1 ? 's' : ''} × ₹${fmt(serviceDetails.basePrice)}`;
          }
          break;
        }
        case 'curtain': {
          // Multi-size description with the per-sqft rate appended so the
          // line reads like the sofa breakdown ("2 seats × ₹1,100"). Eg:
          //   single → "8×6 ft × 4 panels × ₹70/sq.ft"
          //   multi  → "10 panels across 3 sizes (192 sq.ft × ₹70/sq.ft)"
          baseServiceDescription = curtainDescription(
            serviceMeasurements,
            serviceDetails.basePrice
          );
          {
            const rows = curtainLineItems(serviceMeasurements, serviceDetails.basePrice);
            if (rows.length > 1) {
              baseServiceItems = rows;
              baseServiceRate = serviceDetails.basePrice;
            }
          }
          break;
        }
        case 'carpet': {
          // Same pattern as curtain — append the live basePrice so the
          // customer sees the area × rate math inline.
          baseServiceDescription = carpetDescription(serviceMeasurements, serviceDetails.basePrice);
          {
            // >1 size → show each carpet as its own auditable line so the
            // headline amount reconciles size-by-size.
            const rows = carpetLineItems(serviceMeasurements, serviceDetails.basePrice);
            if (rows.length > 1) {
              baseServiceItems = rows;
              baseServiceRate = serviceDetails.basePrice;
            }
          }
          break;
        }
      }
    }

    const addOnsTotal = selectedAddOns.reduce((sum, addOnItem) => {
      return sum + (typeof addOnItem === 'object' && 'price' in addOnItem ? addOnItem.price : 0);
    }, 0);

    // Compute surge fee from selectedDate.
    // Tomorrow & day-after = ₹300, 3+ days out = no surge. Mirrors the
    // server's computeSurgeFee (src/lib/rescheduleSurcharge.ts) so the
    // displayed total matches what the booking is actually billed for.
    // When the admin has switched the surge toggle OFF in CMS, this stays
    // at 0 regardless of the date bucket — the server enforces the same.
    let surgeFee = 0;
    if (selectedDate && surgeEnabled) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selected = new Date(selectedDate + 'T00:00:00');
      const diffDays = Math.round((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      // Today is unbookable (see bookingDateUnavailableReason). Surge
      // window starts from tomorrow (diff 1) and runs through the day
      // after (diff 2). Matches src/lib/rescheduleSurcharge.ts exactly.
      if (diffDays === 1 || diffDays === 2) surgeFee = 300;
    }

    // Per-pincode travel fee from ServiceableArea; default 500 (Delhi flat
    // rate) when no record matches.
    const baseTravelFee =
      typeof addressData.transportCharge === 'number' ? addressData.transportCharge : 500;

    // ---- Multi-service: extras ----
    // Each extra service entry has its own category type + measurements +
    // basePrice from its lazy-fetched details. We compute its line cost the
    // same way the primary does (sofa: seats × price; curtain/carpet: area;
    // upholstery: quantity × price). The pricing sidebar shows a per-line
    // breakdown so the customer sees what each addition costs.
    const extraServiceLines = extraServiceEntries.map((entry) => {
      const det = entry.details;
      const meas = entry.serviceMeasurements;
      const xUnitLabel = meas.measurementUnit === 'meters' ? 'm' : 'ft';
      let cost = 0;
      let description = '';
      // Multi-size carpet/curtain rows for this extra — same auditable
      // per-size treatment as the primary line.
      let items:
        | Array<{
            label?: string;
            width: number;
            length: number;
            quantity: number;
            unitLabel: string;
            areaSqFt: number;
            amount: number;
          }>
        | undefined;
      let rate: number | undefined;
      if (det && entry.categoryType) {
        switch (entry.categoryType) {
          case 'sofa':
            if (meas.numberOfSeats && meas.numberOfSeats > 0) {
              cost = meas.numberOfSeats * det.basePrice;
              description = `${meas.numberOfSeats} seat${meas.numberOfSeats !== 1 ? 's' : ''} × ₹${fmt(det.basePrice)}`;
            }
            break;
          case 'curtain': {
            // Multi-size: same pattern as carpet. Helper handles both the
            // new curtainItems[] array and the legacy single-size shape.
            // Pass det.basePrice so the description includes the per-sqft
            // rate inline (matches the sofa breakdown format).
            const c = curtainLineCost(meas, det.basePrice);
            if (c != null) {
              cost = c;
              description = curtainDescription(meas, det.basePrice);
              const rows = curtainLineItems(meas, det.basePrice);
              if (rows.length > 1) {
                items = rows;
                rate = det.basePrice;
              }
            }
            break;
          }
          case 'carpet': {
            // Multi-size: see carpetMeasurements.ts. Handles both the new
            // carpetItems[] array and the legacy single-size shape. Rate
            // passed through so the sidebar shows "× ₹70/sq.ft" inline.
            const c = carpetLineCost(meas, det.basePrice);
            if (c != null) {
              cost = c;
              description = carpetDescription(meas, det.basePrice);
              const rows = carpetLineItems(meas, det.basePrice);
              if (rows.length > 1) {
                items = rows;
                rate = det.basePrice;
              }
            }
            break;
          }
          case 'upholstery':
            if (meas.quantity && meas.quantity > 0) {
              cost = meas.quantity * det.basePrice;
              const unit = det.unit || 'item';
              description = `${meas.quantity} ${unit}${meas.quantity !== 1 ? 's' : ''} × ₹${fmt(det.basePrice)}`;
            }
            break;
        }
      }
      return { uid: entry.uid, name: cleanServiceTitle(det?.name), cost, description, items, rate };
    });
    const extrasTotal = extraServiceLines.reduce((s, l) => s + l.cost, 0);

    // Service total = primary items + add-ons + every extra service line.
    const servicesTotal = basePrice + addOnsTotal + extrasTotal;

    // Travel fee is informational from the very first step — ₹500 by default
    // (Delhi flat rate), automatically swapping to ₹600 once the customer
    // enters a non-Delhi NCR pincode (AddressDetailsStep updates
    // addressData.transportCharge from the ServiceableArea lookup, which
    // feeds baseTravelFee above). Surge fee, on the other hand, only makes
    // sense once an actual booking is on the table — gated on hasServices.
    const hasServices = servicesTotal > 0;
    const effectiveSurgeFee = hasServices ? surgeFee : 0;

    // Payment-plan incentives — only kick in on the Payment step
    // (currentStep === 6) once the customer has actually picked a plan; on
    // earlier steps we show the neutral, undiscounted total.
    //
    //   - "full"    → 10% off services AND travel fee waived (mirrors the
    //                 server's computePricingServerSide).
    //   - "advance" → travel fee waived (the 10% prepay perk).
    //   - "cod"     → neither.
    const isPaymentStep = currentStep === 6;
    const fullDiscountActive = isPaymentStep && paymentPlan === 'full' && hasServices;
    const payInFullDiscount = fullDiscountActive
      ? Math.round(servicesTotal * PAY_IN_FULL_DISCOUNT_RATE)
      : 0;
    const servicesAfterDiscount = servicesTotal - payInFullDiscount;

    const waiverActive =
      isPaymentStep && (paymentPlan === 'full' || paymentPlan === 'advance') && hasServices;
    const travelFee = waiverActive ? 0 : baseTravelFee;
    const travelWaivedAmount = waiverActive ? baseTravelFee : 0;

    const subtotal = servicesAfterDiscount + travelFee + effectiveSurgeFee;
    const gst = 0;
    const total = subtotal;

    return {
      basePrice,
      // Display-cleaned ("Sofa", not "Sofa Per Seat (Maximum 2ft)") — the
      // description line under it carries the unit math ("3 seats × ₹1,100").
      // Payload serviceName elsewhere stays the raw catalogue SKU.
      baseServiceName: cleanServiceTitle(serviceDetails?.name),
      baseServiceDescription,
      baseServiceItems,
      baseServiceRate,
      // Uniform receipt: a selected-but-unmeasured primary still shows in the
      // sidebar as a muted pending line instead of vanishing until sizes are
      // entered (same treatment as unmeasured extras).
      pendingPrimaryName:
        serviceDetails && basePrice === 0
          ? cleanServiceTitle(serviceDetails.name) || 'Selected service'
          : null,
      servicesTotal,
      payInFullDiscount,
      travelFee,
      travelWaivedAmount,
      furnitureItems: furnitureItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        pricePerUnit: item.pricePerUnit,
        total: item.pricePerUnit * item.quantity,
      })),
      addOns: selectedAddOns
        .map((id) => {
          if (typeof id === 'object' && 'name' in id && 'price' in id) {
            return { name: id.name, price: id.price };
          }
          return null;
        })
        .filter(Boolean) as Array<{ name: string; price: number }>,
      // Per-line cost of each extra service so the sidebar can show a
      // breakdown like "+ Carpet Per Sq.ft × 80 sq.ft → ₹5,600".
      extraServiceLines,
      extrasTotal,
      surgeFee: effectiveSurgeFee,
      subtotal,
      gst,
      total,

      // ---- Display-only fields (honest-sidebar / Mockup A) ----
      // These drive WHAT THE CUSTOMER SEES on steps 1-5; the payload numbers
      // above are untouched and the server re-prices authoritatively anyway.
      //
      // hasServices        → false until the customer configures something;
      //                      the sidebar + mobile bar show an empty state
      //                      instead of a bare ₹500 travel fee.
      // travelWaivableAtCheckout
      //                    → pre-payment-step flag: render the travel fee
      //                      struck-through with "Free with online payment"
      //                      (the payment step really does waive it for the
      //                      full/advance plans — see waiverActive above)
      //                      and keep it OUT of the displayed total. COD
      //                      re-adds it explicitly at checkout.
      // displaySubtotal    → services only. The old "Subtotal" row lumped
      //                      travel + surge in, so an empty cart read
      //                      "Subtotal ₹500" — semantically wrong.
      // displayTotal       → what the total row + mobile bar show. On the
      //                      payment step it equals the real plan-aware
      //                      total; earlier it's services + surge.
      hasServices,
      travelWaivableAtCheckout: !isPaymentStep && hasServices,
      displaySubtotal: servicesAfterDiscount,
      displayTotal: hasServices
        ? isPaymentStep
          ? total
          : servicesAfterDiscount + effectiveSurgeFee
        : 0,
    };
  };

  const handleAddOnToggle = (addOnId: string | { id: string; name: string; price: number }) => {
    setSelectedAddOns((prev) => {
      const idToCheck = typeof addOnId === 'string' ? addOnId : addOnId.id;
      const isAlreadySelected = prev.some(
        (item) => (typeof item === 'string' ? item : item.id) === idToCheck
      );
      if (isAlreadySelected) {
        return prev.filter((item) => (typeof item === 'string' ? item : item.id) !== idToCheck);
      }
      return [...prev, addOnId];
    });
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: {
        // Step 1 now bundles service selection + per-category measurement.
        // Both must be in place to advance — picking only a service without
        // entering at least one measurement leaves the cart at ₹0.
        // Same helper the config sheet's CTA and the summary strips use, so
        // "Continue is blocked" can never disagree with "this line looks set
        // up" on the page.
        const isMeasurementValid = measurementValidFor;
        if (!selectedService || !selectedPackage) return false;
        if (!selectedCategoryType || !serviceDetails) return false;
        if (!isMeasurementValid(selectedCategoryType, serviceMeasurements)) return false;
        // AI measurement mode is opt-in but, once chosen, mandatory: the
        // customer must have completed the inline photo verification
        // (verifiedMap.primary) before Continue. Manual mode skips this.
        if (primaryMeasurementMode === 'ai' && !verifiedMap.primary) return false;

        // Every ADDITIONAL service must be complete too. A half-filled
        // extra (category picked but no measurements yet) blocks the
        // Continue button so the customer can't accidentally submit a
        // booking with a zero-priced extra line. AI-mode extras must also
        // have passed their inline photo verification.
        for (const extra of extraServiceEntries) {
          if (!extra.serviceId || !extra.packageId || !extra.categoryType) return false;
          if (!extra.details) return false;
          if (!isMeasurementValid(extra.categoryType, extra.serviceMeasurements)) return false;
          if (extra.measurementMode === 'ai' && !verifiedMap[extra.uid]) return false;
        }
        return true;
      }
      // ──────────────────────────────────────────────────────────────
      // STEP 2 (Add-ons) IS CURRENTLY SKIPPED — see handleProceed.
      // The validation case is kept (commented) so restoring the Add-ons
      // step is a one-liner: just uncomment.
      // ──────────────────────────────────────────────────────────────
      // case 2:
      //   // Step 2 is now Add-ons (optional). Always valid — customer can
      //   // skip if no add-on appeals.
      //   return true;
      case 3: {
        // Step 3 valid only when EVERY service is verified — primary plus
        // every extra the customer stacked on Step 1. Each entry sets its
        // own flag in `verifiedMap` from inside its MeasurementVerificationStep.
        if (!verifiedMap.primary) return false;
        for (const extra of extraServiceEntries) {
          if (!extra.packageId || !extra.categoryType || !extra.details) continue;
          if (!verifiedMap[extra.uid]) return false;
        }
        return true;
      }
      case 4:
        return selectedDate && selectedSlot;
      case 5: {
        const result = addressSchema.safeParse(addressData);
        return (
          result.success &&
          !!addressData.latitude &&
          !!addressData.longitude &&
          addressData.locationSaved === true
        );
      }
      case 6:
        return true; // Payment step is always valid
      default:
        return false;
    }
  };

  const handleProceed = () => {
    const maxStep = 6; // Service+Measurements(1) → Schedule(4) → Address(5) → Payment(6)
    if (currentStep < maxStep && isStepValid()) {
      // Steps 2 (Add-ons) AND 3 (standalone Verification) are both skipped.
      // Add-ons was retired earlier; AI verification now lives INLINE in
      // Step 1 as the optional "AI" measurement mode, so the flow jumps
      // 1 → 4 (Schedule) directly. Internal step numbers (4/5/6) are kept
      // so Schedule/Address/Payment render blocks don't need renumbering.
      const nextStep = currentStep === 1 ? 4 : currentStep + 1;
      // InitiateCheckout — fire once on the FIRST transition into step 6
      // (the payment step). This is Meta's standard funnel signal that the
      // visitor reached checkout — it powers checkout-optimised audiences
      // and Advantage+ campaign learning.
      if (nextStep === 6 && currentStep !== 6) {
        trackStandardEvent('InitiateCheckout', {
          content_name: selectedService || 'Magia service',
          content_category: selectedCategoryType || 'service',
          value: pricing.total,
          currency: 'INR',
          num_items: 1,
        });
      }
      setCurrentStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ------------------------------------------------------------------
  // Booking payload builder — extracted so it can be used twice: first
  // when creating the draft booking BEFORE charging the customer
  // (paymentType seeded from the chosen plan, no payment fields), and
  // again as a reference for the payment record. Splitting add-ons by
  // packageId prefix keeps each service's add-ons against its OWN
  // catalogue.
  // ------------------------------------------------------------------
  const buildBookingPayload = (paymentTypeOverride: string, paymentData: any = {}) => {
    if (!selectedPackage) {
      throw new Error('Please select a service package');
    }

    const addOnsForPackage = (pkgId: string) =>
      selectedAddOns
        .filter(
          (ao: any) =>
            typeof ao === 'object' && typeof ao.id === 'string' && ao.id.startsWith(`${pkgId}-`)
        )
        .map((ao: any) => ({
          addOnId: ao.id,
          name: ao.name,
          price: ao.price,
          quantity: 1,
        }));

    const unprefixedAddOns = selectedAddOns
      .filter((ao: any) => typeof ao === 'string')
      .map((name: any) => ({ name, price: 0 }));

    return {
      service: selectedPackage, // selectedPackage contains the service ID
      package: selectedPackage, // Using service ID as package ID temporarily (should be actual package ID)
      quantity:
        selectedCategoryType === 'sofa'
          ? serviceMeasurements.numberOfSeats || 1
          : selectedCategoryType === 'curtain'
            ? curtainTotalPanels(serviceMeasurements) || 1
            : selectedCategoryType === 'carpet'
              ? carpetTotalCount(serviceMeasurements) || 1
              : selectedCategoryType === 'upholstery'
                ? serviceMeasurements.quantity || 1
                : furnitureItems.reduce((sum, item) => sum + item.quantity, 0) || 1,
      measurements: {
        ...smartMeasurements,
        serviceMeasurements: serviceMeasurements,
      },
      furnitureItems: furnitureItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        pricePerUnit: item.pricePerUnit,
        total: item.pricePerUnit * item.quantity,
      })),
      addOns: [...addOnsForPackage(selectedPackage), ...unprefixedAddOns],
      scheduledDate: selectedDate,
      scheduledSlot: {
        startTime: selectedSlot.split('-')[0]?.trim() || '',
        endTime: selectedSlot.split('-')[1]?.trim() || '',
      },
      customerDetails: {
        ...addressData,
        // Ensure email and phone are included from user if available
        email: addressData.email || user?.email || '',
        phone: addressData.phone || user?.phone || '',
        fullName: addressData.fullName || user?.name || '',
        coordinates:
          addressData.latitude && addressData.longitude
            ? {
                latitude: addressData.latitude,
                longitude: addressData.longitude,
              }
            : undefined,
      },
      pricing: pricing,
      surgeFee: pricing.surgeFee || 0,
      // paymentType reflects the customer's chosen plan (full / advance /
      // cod). paymentStatus/paidAmount/remainingAmount are server-controlled
      // — the route always seeds pending/0/total at draft creation, and
      // /api/payments flips them once a payment record is linked. Omitting
      // them keeps the client from racing the source of truth.
      paymentType: paymentTypeOverride,
      // Server re-validates the coupon and recomputes the discount from
      // the canonical coupon record — never trust the client's value.
      ...(paymentData.couponCode ? { couponCode: paymentData.couponCode } : {}),
      // Optional B2B GST billing captured on the payment step. Prices are
      // already GST-inclusive, so we send ONLY gstDetails.enabled (never
      // applyGst) — the server persists the GSTIN + business name for the tax
      // invoice without adding any 18% on top.
      ...(paymentData.gstDetails?.enabled
        ? {
            gstDetails: {
              enabled: true,
              gstin: paymentData.gstDetails.gstin || '',
              businessName: paymentData.gstDetails.businessName || '',
            },
          }
        : {}),
      totalAmount: pricing.total,
      // Include customerId if user is logged in
      ...(user?.id && user?.role === 'customer' ? { customerId: user.id } : {}),
      // Include AI verification data if available
      ...(aiVerificationData ? { aiVerification: aiVerificationData } : {}),
      // Multi-service: bundle every extra service into the same booking.
      // Each entry is keyed by its own packageId + measurements; the
      // server re-validates and re-prices them line-by-line against the
      // catalogue (same path the admin manual booking uses).
      ...(extraServiceEntries.length > 0
        ? {
            extraServices: extraServiceEntries
              .filter((e) => e.packageId && e.categoryType && e.details)
              .map((e) => {
                const m = e.serviceMeasurements;
                // Quantity hint for synthetic/per-unit services (sofa per
                // seat, headboard, etc.). Area-priced services don't use
                // it on the server; their measurements drive the math.
                let quantity = 1;
                if (e.categoryType === 'sofa' && m.numberOfSeats) {
                  quantity = m.numberOfSeats;
                } else if (e.categoryType === 'upholstery' && m.quantity) {
                  quantity = m.quantity;
                } else if (e.categoryType === 'curtain') {
                  const p = curtainTotalPanels(m);
                  if (p > 0) quantity = p;
                } else if (e.categoryType === 'carpet') {
                  const c = carpetTotalCount(m);
                  if (c > 0) quantity = c;
                }
                const extraAi = aiVerificationMap[e.uid];
                return {
                  service: e.packageId,
                  package: e.packageId, // service-as-package convention
                  quantity,
                  // Stash this extra's AI verification inside its
                  // measurements blob. The bookingServiceItemSchema's
                  // `measurements` is a free-form Map, so the server
                  // persists it onto the booking's services[] entry
                  // without any schema change.
                  measurements: {
                    serviceMeasurements: m,
                    ...(extraAi ? { aiVerification: extraAi } : {}),
                  },
                  // Each extra carries its own add-ons selected via the
                  // ServiceAddOnsBlock on Step 2. The server validates
                  // them against THIS extra's catalogue (not the primary's).
                  addOns: addOnsForPackage(e.packageId),
                };
              }),
          }
        : {}),
    };
  };

  // ------------------------------------------------------------------
  // createBookingDraft — POST /api/bookings BEFORE charging the customer.
  // Returns the booking's Mongo _id so the payment record (created after
  // Razorpay succeeds) can link to it. If the booking POST fails (cart
  // mismatch, slot taken, schema error, etc.) the customer sees the error
  // BEFORE Razorpay ever opens — so we can never end up with money
  // captured but no booking record. Cached via createdBookingDocId so a
  // retry (close Razorpay → click Pay again) reuses the same draft.
  // ------------------------------------------------------------------
  const createBookingDraft = async (paymentTypeOverride: string, paymentData: any = {}) => {
    // Signature combines every input whose change should invalidate the
    // cached draft: payment plan, coupon, total. If a prior draft was
    // created with a different signature it's stale — POST a fresh one.
    const g = paymentData?.gstDetails;
    const gstSig = g?.enabled ? `gst:${g.gstin || ''}|${g.businessName || ''}` : '';
    const signature = [paymentTypeOverride, paymentData?.couponCode || '', pricing.total, gstSig].join(
      '|'
    );
    if (createdBookingDocId && createdBookingSignature === signature) {
      return createdBookingDocId;
    }

    const payload = buildBookingPayload(paymentTypeOverride, paymentData);

    // Shared event_id between client pixel + server CAPI for Schedule dedup.
    // The server reads this off the header and POSTs an identical CAPI event
    // so Meta collapses the two into ONE conversion record.
    const scheduleEventId = newEventId();

    const res = await fetch('/api/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-meta-event-id': scheduleEventId,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create booking');
    }
    const booking = await res.json();
    const docId = booking._id || booking.id;
    setCreatedBookingDocId(docId);
    setCreatedBookingSignature(signature);
    if (booking.bookingId) setCreatedBookingId(booking.bookingId);
    // Schedule — the canonical "booking created" conversion. Pixel +
    // server CAPI dedup via scheduleEventId.
    trackStandardEvent(
      'Schedule',
      {
        content_name: booking.serviceName || selectedService || 'Magia service',
        content_category: selectedCategoryType || 'service',
        content_ids: [String(docId)],
        value: pricing.total,
        currency: 'INR',
      },
      scheduleEventId
    );
    // Guest bookings come back with a token; logged-in ones don't.
    if (booking.guestAccessToken) {
      setGuestAccessToken(booking.guestAccessToken);
      guestAccessTokenRef.current = booking.guestAccessToken;
    }
    return docId;
  };

  // ------------------------------------------------------------------
  // handlePaymentSuccess — runs AFTER Razorpay (or COD path) confirms.
  // The booking already exists (createBookingDraft was called first), so
  // this just links the payment record. /api/payments updates the
  // booking's paymentStatus to 'paid' / 'partial'. For COD there's no
  // payment record at all — the booking stays paymentStatus='pending'
  // until the worker collects on completion.
  // ------------------------------------------------------------------
  const handlePaymentSuccess = async (paymentData: any) => {
    try {
      const bookingDocId = paymentData.bookingDocId || createdBookingDocId;
      if (!bookingDocId) {
        throw new Error('Booking draft was not created — please retry');
      }

      // OpenAI Pixel conversion — "the customer booked an appointment".
      //
      // Fired HERE rather than next to the Meta `Schedule` event, because
      // Schedule fires when the DRAFT booking is created (before payment), so
      // an abandoned checkout would report a conversion that never happened.
      // handlePaymentSuccess runs only once Razorpay OR the COD path has
      // confirmed, and both paths pass through this single point above the
      // online-only payment branch below — so the event fires exactly once
      // per real appointment, for both payment types.
      //
      // This component is only used by the customer-facing flow; admin manual
      // bookings go through CreateBookingForm, so team-entered jobs correctly
      // never count as ad conversions.
      measureOpenAiEvent('appointment_scheduled', { type: 'customer_action' });

      // Mark the abandoned-checkout lead as converted so it drops out of
      // the admin Remarket tab. Fire-and-forget — a network blip here must
      // not block the post-payment redirect.
      if (abandonedLeadId) {
        fetch(`/api/leads/abandoned-checkout/${abandonedLeadId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ convertedBookingId: bookingDocId }),
          keepalive: true,
        }).catch(() => {});
      }

      if (paymentData.status === 'completed' && paymentData.transactionId) {
        const paymentPayload: any = {
          booking: bookingDocId,
          amount: paymentData.amount,
          currency: 'INR',
          paymentMethod: paymentData.paymentMethod || 'card',
          paymentGateway: 'razorpay',
          paymentType: paymentData.paymentType || 'full_payment',
          totalAmount: paymentData.totalAmount || pricing.total,
          remainingAmount: paymentData.remainingAmount || 0,
          transactionId: paymentData.transactionId,
          gatewayOrderId: paymentData.gatewayOrderId,
          gatewayPaymentId: paymentData.gatewayPaymentId,
          razorpaySignature: paymentData.razorpaySignature,
          status: 'completed',
        };

        const userIdToSave =
          paymentData.userId || (user?.id && user?.role === 'customer' ? user.id : undefined);
        if (userIdToSave) paymentPayload.userId = userIdToSave;

        // Shared event_id between browser Purchase pixel + server CAPI.
        // /api/payments reads `x-meta-event-id` and fires the matching CAPI
        // event with the same id so Meta dedups them.
        const purchaseEventId = newEventId();

        const paymentRes = await fetch('/api/payments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': user?.id || '',
            'x-user-role': user?.role || '',
            'x-meta-event-id': purchaseEventId,
          },
          body: JSON.stringify(paymentPayload),
        });

        // Purchase — fire the browser pixel immediately on the payment-record
        // POST. Server CAPI fires from /api/payments using the same
        // purchaseEventId so Meta collapses them to one. We don't wait for the
        // response to fire the pixel (a stuck record write shouldn't lose the
        // conversion signal — Razorpay already confirmed the charge).
        trackStandardEvent(
          'Purchase',
          {
            content_name: createdBookingId || String(bookingDocId),
            value: Number(paymentData.amount || 0),
            currency: 'INR',
            content_ids: [String(bookingDocId)],
            content_type: 'product',
            num_items: 1,
          },
          purchaseEventId
        );

        if (!paymentRes.ok) {
          // Booking is already saved; surface the issue but don't undo it.
          // Show the SERVER'S actual reason (signature/amount/capture/dupe) —
          // a generic message here hid the real cause through multiple
          // debugging rounds. The detail is what tells support (and us) which
          // gate rejected it.
          const errorData = await paymentRes.json().catch(() => ({}));
          console.error('Failed to create payment record:', paymentRes.status, errorData);
          const reason = errorData?.error || errorData?.message || `HTTP ${paymentRes.status}`;
          alert(
            `Your booking was saved, but recording the payment failed.\n\nReason: ${reason}\n\nReference: ${bookingDocId}\nPlease screenshot this and send it to support — your payment is safe and will be reconciled.`
          );
        }
      }

      // Where to land after payment:
      //   - Logged-in CUSTOMER → their dashboard (it tracks their bookings).
      //   - Guest checkout → the token-guarded, bookmarkable confirmation
      //     page (same link as the email; their only way back, no account).
      //   - A session that ISN'T a customer (e.g. an admin testing the flow)
      //     → has no guest token AND no customer dashboard, so
      //     /customer-dashboard would bounce to /login. Send them to the
      //     confirmation page by bookingId instead.
      const isLoggedInCustomer = !!(user?.id && user?.role === 'customer');
      const guestToken =
        paymentData.guestAccessToken || guestAccessTokenRef.current || guestAccessToken;
      if (isLoggedInCustomer) {
        router.push('/customer-dashboard');
      } else if (guestToken) {
        router.push(`/booking-confirmation?token=${encodeURIComponent(guestToken)}`);
      } else {
        const confirmationId = paymentData.bookingId || createdBookingId || bookingDocId;
        router.push(`/booking-confirmation?bookingId=${encodeURIComponent(confirmationId)}`);
      }
    } catch (error: any) {
      console.error('Post-payment error:', error);
      alert(error.message || 'Failed to complete booking. Please try again.');
    }
  };

  const handlePaymentError = (error: string) => {
    setIsPaymentProcessing(false); // Reset processing state on error
    alert(error);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Leaving the payment step invalidates any draft booking we created
      // — the customer might tweak the cart, change the slot, or apply a
      // different coupon. Next "Pay" click will create a fresh draft.
      if (currentStep === 6) setCreatedBookingDocId(null);
      // Steps 2 (Add-ons) and 3 (Verification) are skipped — going back
      // from Schedule (4) lands on Service & Measurements (1), not 3/2.
      const prevStep = currentStep === 4 ? 1 : currentStep - 1;
      setCurrentStep(prevStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (step: number) => {
    if (currentStep === 6 && step !== 6) setCreatedBookingDocId(null);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pricing = calculatePricing();

  // ── ServiceConfigSheet derived state (Step 1) ──────────────────────────
  // Primary-service line validity + cost, mirrored from isStepValid /
  // calculatePricing so the sheet CTA, the page summary strip, and the
  // Continue gate always agree.
  const primaryMeasurementValid =
    !!selectedCategoryType &&
    !!serviceDetails &&
    measurementValidFor(selectedCategoryType, serviceMeasurements);
  const primaryLineTotal =
    selectedCategoryType && serviceDetails
      ? lineTotalFor(selectedCategoryType, serviceMeasurements, serviceDetails.basePrice)
      : 0;
  const primaryQtySummary = qtySummaryFor(selectedCategoryType, serviceMeasurements);

  // The extra the sheet is currently editing, if any. Looked up fresh each
  // render so the sheet's footer total tracks live edits.
  const activeExtra =
    sheetTarget?.kind === 'extra'
      ? extraServiceEntries.find((e) => e.uid === sheetTarget.uid) || null
      : null;

  // Sheet props resolve from whichever line is targeted. One set of props,
  // two possible sources — the sheet itself stays presentational.
  const sheetCategoryType = activeExtra ? activeExtra.categoryType : selectedCategoryType;
  const sheetDetails = activeExtra ? activeExtra.details : serviceDetails;
  const sheetMeasurements = activeExtra ? activeExtra.serviceMeasurements : serviceMeasurements;
  const sheetMode = activeExtra ? activeExtra.measurementMode : primaryMeasurementMode;
  const sheetVerifyKey = activeExtra ? activeExtra.uid : 'primary';
  const sheetMeasurementValid =
    !!sheetCategoryType &&
    !!sheetDetails &&
    measurementValidFor(sheetCategoryType, sheetMeasurements);
  const sheetLineTotal =
    sheetCategoryType && sheetDetails
      ? lineTotalFor(sheetCategoryType, sheetMeasurements, sheetDetails.basePrice)
      : 0;
  // AI mode additionally requires the inline photo verification, exactly
  // like the Continue gate in isStepValid case 1.
  const sheetAddDisabled =
    !sheetMeasurementValid || (sheetMode === 'ai' && !verifiedMap[sheetVerifyKey]);
  const sheetHint = !sheetCategoryType
    ? 'Pick a service to continue'
    : !sheetMeasurementValid
      ? measurementHintFor(sheetCategoryType)
      : sheetMode === 'ai' && !verifiedMap[sheetVerifyKey]
        ? 'Complete the AI photo verification to continue'
        : '';
  const sheetSubtitle = sheetSubtitleFor(sheetCategoryType, sheetDetails);
  const sheetTitle = activeExtra
    ? cleanServiceTitle(activeExtra.details?.name) || 'Add a service'
    : cleanServiceTitle(serviceDetails?.name) || 'Set up your service';

  // Closing discards a never-configured extra: an entry with no service
  // picked would otherwise sit in the list as an empty row and silently
  // block Continue (isStepValid rejects extras without a packageId).
  const closeSheet = () => {
    if (sheetTarget?.kind === 'extra') {
      const uid = sheetTarget.uid;
      setExtraServiceEntries((prev) => prev.filter((e) => e.uid !== uid || !!e.packageId));
    }
    setSheetTarget(null);
  };

  const handleSheetAdd = () => {
    if (sheetAddDisabled) return;
    // AddToCart — the sheet's confirm is the cleanest cart signal in the
    // funnel, sitting between the card tap and InitiateCheckout. Extras
    // already fired their own AddToCart at add-time (addCrossSellService),
    // so only the primary fires here — no double-counting.
    if (!activeExtra) {
      trackStandardEvent('AddToCart', {
        content_name: serviceDetails?.name || 'Magia service',
        content_category: selectedCategoryType || 'service',
        value: primaryLineTotal,
        currency: 'INR',
      });
    }
    setSheetTarget(null);
  };

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1670:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1670" data-component-end-line="2545" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gradient-to-b%20from-white%20to-brand-50%2F30%20pt-24%20lg%3Apt-28%22%7D" className="min-h-screen bg-gradient-to-b from-white to-brand-50/30 pt-24 lg:pt-28">
      {/* Steps 2 ("Add-ons") AND 3 ("Verification") are both skipped now:
          Add-ons was retired, and AI verification moved INLINE into Step 1
          as an optional measurement mode. The indicator shows the 4-step
          subset; internal step numbers (1, 4, 5, 6) are remapped to the
          visible 1..4 indices and back. To restore the standalone
          Verification step, re-add 'Verification' to the steps list and
          revert the displayStep / handleStepClick maths below.
          Original 6-step list (preserved for easy restore):
          ['Service & Measurements', 'Add-ons', 'Verification', 'Schedule', 'Address Details', 'Payment'] */}
      <BookingProgressIndicator data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1680:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1680" data-component-end-line="1690" data-component-file="BookingFlowInteractive.tsx" data-component-name="BookingProgressIndicator" data-component-content="%7B%22elementName%22%3A%22BookingProgressIndicator%22%7D"
        currentStep={currentStep <= 1 ? 1 : currentStep - 2}
        onStepClick={(clickedIdx) => {
          // Indicator passes a 1..4 index into the visible-step list.
          // Map back to internal step numbers (1, 4, 5, 6).
          const internalStep = clickedIdx === 1 ? 1 : clickedIdx + 2;
          handleStepClick(internalStep);
        }}
        allowBackNavigation={true}
        steps={['Service & Measurements', 'Schedule', 'Address Details', 'Payment']}
      />

      {/* NCR-only service notice — visible at every step */}
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1693:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1693" data-component-end-line="1734" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20md%3Apx-12%20mt-4%22%7D" className="max-w-7xl mx-auto px-6 md:px-12 mt-4">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1694:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1694" data-component-end-line="1733" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%20p-3%20sm%3Ap-4%20rounded-xl%20bg-amber-50%20border%20border-amber-200%22%7D"
          className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-amber-50 border border-amber-200"
          data-testid="ncr-only-notice"
        >
          <svg data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1698:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1698" data-component-end-line="1716" data-component-file="BookingFlowInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-amber-600%20flex-shrink-0%20mt-0.5%22%7D"
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1706:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1706" data-component-end-line="1710" data-component-file="BookingFlowInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1711:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1711" data-component-end-line="1715" data-component-file="BookingFlowInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1717:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1717" data-component-end-line="1732" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%22%7D" className="text-sm">
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1718:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1718" data-component-end-line="1718" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-amber-900%22%2C%22textContent%22%3A%22Service%20area%3A%22%7D" className="font-semibold text-amber-900">Service area:</span>{' '}
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1719:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1719" data-component-end-line="1731" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-amber-800%22%2C%22textContent%22%3A%22Online%20booking%20is%20currently%20available%20only%20in%20.%20Outside%20NCR%3F%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20Contact%20us%20on%20%20%20%20%20and%20we'll%20let%20you%20know%20when%20we%20expand%20to%20your%20city.%22%7D" className="text-amber-800">
              Online booking is currently available only in <strong data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1720:60" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1720" data-component-end-line="1720" data-component-file="BookingFlowInteractive.tsx" data-component-name="strong" data-component-content="%7B%22elementName%22%3A%22strong%22%2C%22textContent%22%3A%22Delhi%20NCR%22%7D">Delhi NCR</strong>. Outside NCR?
              Contact us on{' '}
              <a data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1722:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1722" data-component-end-line="1729" data-component-file="BookingFlowInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Fapi.whatsapp.com%2Fsend%3Fphone%3D918810553713%26text%3DHi%2520Magia%252C%2520I%25E2%2580%2599m%2520outside%2520Delhi%2520NCR%2520and%2520wanted%2520to%2520enquire%2520about%2520your%2520services.%22%2C%22className%22%3A%22underline%20font-semibold%20hover%3Atext-amber-900%22%2C%22textContent%22%3A%22WhatsApp%22%7D"
                href="https://api.whatsapp.com/send?phone=918810553713&text=Hi%20Magia%2C%20I%E2%80%99m%20outside%20Delhi%20NCR%20and%20wanted%20to%20enquire%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold hover:text-amber-900"
              >
                WhatsApp
              </a>{' '}
              and we'll let you know when we expand to your city.
            </span>
          </div>
        </div>
      </div>

      {/* pb-44 on mobile reserves room for the fixed bottom bar. Without it
          the last ~230px of the form sat permanently underneath the bar at
          maximum scroll — on the address step that buried "Pin Location on
          Map", so the page felt frozen with no way to continue. */}
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1740:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1740" data-component-end-line="2544" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20md%3Apx-12%20py-10%20lg%3Apy-16%20pb-44%20lg%3Apb-16%22%7D" className="max-w-7xl mx-auto px-6 md:px-12 py-10 lg:py-16 pb-44 lg:pb-16">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1741:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1741" data-component-end-line="2448" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-10%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <motion.div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1742:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1742" data-component-end-line="2423" data-component-file="BookingFlowInteractive.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-8%22%7D"
            className="lg:col-span-2 space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {currentStep === 1 && (
              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1749:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1749" data-component-end-line="2295" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-10%22%7D" className="space-y-10">
                {/* Service / package picker — add-ons block suppressed; they
                    now live on a dedicated Step 2. */}
                <ServiceSelectionStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1752:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1752" data-component-end-line="1861" data-component-file="BookingFlowInteractive.tsx" data-component-name="ServiceSelectionStep" data-component-content="%7B%22elementName%22%3A%22ServiceSelectionStep%22%7D"
                  selectedService={selectedService}
                  selectedPackage={selectedPackage}
                  selectedAddOns={selectedAddOns}
                  furnitureItems={furnitureItems}
                  onServiceChange={(serviceId, categoryType, seed) => {
                    // ---- Cart semantics (bug fix) ----
                    // The grid used radio-button behaviour: tapping a second
                    // card REPLACED the primary service, silently throwing
                    // the previous one away. With a CONFIGURED primary, a
                    // tap on a different service now ADDS it to the booking
                    // (same pre-seeded path as the cross-sell strip), and a
                    // tap on a service already in the cart scrolls to its
                    // card instead of duplicating it. A true replace remains
                    // possible only while the current primary is still
                    // unconfigured (qty 0) — replacing nothing loses nothing.
                    if (
                      serviceId !== selectedService &&
                      selectedService &&
                      primaryMeasurementValid
                    ) {
                      const existing = extraServiceEntries.find(
                        (e) => e.serviceId === serviceId || e.packageId === serviceId
                      );
                      if (existing) {
                        // Already in the cart — reopen its configurator
                        // rather than duplicating the line.
                        setSheetTarget({ kind: 'extra', uid: existing.uid });
                        return;
                      }
                      addCrossSellService({
                        _id: serviceId,
                        name: seed?.name || '',
                        type: categoryType as
                          | 'sofa'
                          | 'curtain'
                          | 'carpet'
                          | 'upholstery'
                          | undefined,
                        basePrice: seed?.basePrice,
                        unit: seed?.unit,
                      });
                      return;
                    }

                    // True replace of an unconfigured primary — reset the
                    // measurements + verification whenever the service
                    // actually changes, so sofa seats can't leak into a
                    // carpet configuration (mirrors the AI-toggle reset).
                    if (serviceId !== selectedService) {
                      setServiceMeasurements((prev) => ({
                        measurementUnit: prev.measurementUnit,
                        images: [],
                      }));
                      setVerifiedMap((m) => ({ ...m, primary: false }));
                      setVerificationComplete(false);
                      setAiVerificationData(null);
                      setAiVerificationMap((m) => ({ ...m, primary: null }));
                    }
                    setSelectedService(serviceId);
                    // Card taps select service AND package in one gesture
                    // (each customer-facing card IS a single-package Service
                    // doc). ServiceSelectionStep no longer fires
                    // onPackageChange from the card click — doing so
                    // unconditionally clobbered the primary's package/details
                    // on ADDITIVE taps, Franken-swapping its identity.
                    setSelectedPackage(serviceId);
                    if (categoryType) {
                      setSelectedCategoryType(
                        categoryType as 'sofa' | 'curtain' | 'carpet' | 'upholstery'
                      );
                    }
                    // Optimistic seed — the tapped card already knows the
                    // service's name + base price, so the sheet renders its
                    // configurator INSTANTLY instead of sitting on
                    // "Loading service…" for a network round trip (1-3s on
                    // mobile data; forever in local dev without a DB). The
                    // /api/services/:id fetch still runs and silently
                    // overwrites with server truth, and the server re-prices
                    // authoritatively at booking-create regardless.
                    if (seed && typeof seed.basePrice === 'number' && seed.basePrice > 0) {
                      const unitFallback =
                        categoryType === 'sofa'
                          ? 'seat'
                          : categoryType === 'carpet' || categoryType === 'curtain'
                            ? 'sq.ft'
                            : 'Pc';
                      setServiceDetails({
                        name: seed.name,
                        basePrice: seed.basePrice,
                        unit: seed.unit || unitFallback,
                      });
                    }
                    // Card tap opens the configurator sheet (Urban Company
                    // pattern) — the tap and its next action stay in the
                    // same viewport instead of the config rendering below
                    // the whole grid.
                    setSheetTarget({ kind: 'primary' });
                  }}
                  onPackageChange={setSelectedPackage}
                  onAddOnToggle={handleAddOnToggle}
                  onFurnitureQuantityChange={handleFurnitureQuantityChange}
                  hideAddOns
                  hideCrossSell
                  hideInlineDetail
                  onCatalogLoaded={setCatalog}
                  extraSelectedIds={extraServiceEntries
                    .map((e) => e.serviceId || e.packageId)
                    .filter(Boolean)}
                />

                {/* Primary-service summary strip — replaces the old inline
                    configurator that rendered below the whole grid. Shows
                    what's been set up (qty + line total) with an Edit button
                    that reopens the sheet; before setup it's an explicit
                    "Set up" prompt so Continue is never a silent dead end. */}
                {selectedCategoryType && serviceDetails && (
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1869:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1869" data-component-end-line="1898" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border-%5B1.5px%5D%20border-%5B%23204653%5D%2F20%20bg-white%20p-4%20md%3Ap-5%20flex%20items-center%20justify-between%20gap-4%20shadow-%5B0_2px_12px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D" className="rounded-2xl border-[1.5px] border-[#204653]/20 bg-white p-4 md:p-5 flex items-center justify-between gap-4 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1870:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1870" data-component-end-line="1889" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
                      <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1871:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1871" data-component-end-line="1873" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-semibold%20text-%5B%231D1D1F%5D%20truncate%22%7D" className="font-semibold text-[#1D1D1F] truncate">
                        {cleanServiceTitle(serviceDetails.name)}
                      </p>
                      {primaryMeasurementValid ? (
                        <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1875:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1875" data-component-end-line="1883" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%20mt-0.5%22%2C%22textContent%22%3A%22%C2%B7%22%7D" className="text-sm text-[#86868B] mt-0.5">
                          {primaryQtySummary} ·{' '}
                          <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1877:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1877" data-component-end-line="1879" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-%5B%23204653%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="font-semibold text-[#204653]">
                            ₹{primaryLineTotal.toLocaleString('en-IN')}
                          </span>
                          {primaryMeasurementMode === 'ai' && !verifiedMap.primary && (
                            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1881:28" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1881" data-component-end-line="1881" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-amber-700%22%2C%22textContent%22%3A%22%C2%B7%20AI%20verification%20pending%22%7D" className="ml-2 text-amber-700">· AI verification pending</span>
                          )}
                        </p>
                      ) : (
                        <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1885:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1885" data-component-end-line="1887" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-amber-700%20mt-0.5%22%2C%22textContent%22%3A%22Not%20set%20up%20yet%20%E2%80%94%20tap%20Set%20up%20to%20add%20quantity%22%7D" className="text-sm text-amber-700 mt-0.5">
                          Not set up yet — tap Set up to add quantity
                        </p>
                      )}
                    </div>
                    <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1890:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1890" data-component-end-line="1897" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex-shrink-0%20px-5%20py-2.5%20rounded-full%20border-2%20border-%5B%23204653%5D%20text-%5B%23204653%5D%20text-sm%20font-semibold%20hover%3Abg-%5B%23E4EDF0%5D%20transition-colors%22%7D"
                      type="button"
                      onClick={() => setSheetTarget({ kind: 'primary' })}
                      className="flex-shrink-0 px-5 py-2.5 rounded-full border-2 border-[#204653] text-[#204653] text-sm font-semibold hover:bg-[#E4EDF0] transition-colors"
                      data-testid="edit-primary-service"
                    >
                      {primaryMeasurementValid ? 'Edit' : 'Set up'}
                    </button>
                  </div>
                )}

                {/* Urban Company-style configurator sheet — bottom sheet on
                    mobile, right drawer on desktop. Hosts the measurement-
                    mode selector + per-category measurement UI that used to
                    render inline below the grid. Opens on card tap; "Add to
                    estimate" fires the AddToCart pixel and closes. */}
                <ServiceConfigSheet data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1906:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1906" data-component-end-line="2097" data-component-file="BookingFlowInteractive.tsx" data-component-name="ServiceConfigSheet" data-component-content="%7B%22elementName%22%3A%22ServiceConfigSheet%22%7D"
                  open={
                    !!sheetTarget &&
                    (sheetTarget.kind === 'extra' ? !!activeExtra : !!selectedCategoryType)
                  }
                  onClose={closeSheet}
                  title={sheetTitle}
                  subtitle={sheetSubtitle}
                  priceLabel={`₹${sheetLineTotal.toLocaleString('en-IN')}`}
                  addDisabled={sheetAddDisabled}
                  disabledHint={sheetHint}
                  onAdd={handleSheetAdd}
                >
                  {/* ── Extra service ── the same configurator the primary
                      gets, driven off the targeted entry. ExtraServiceCard
                      drops its own card chrome in sheet mode (the sheet
                      supplies the header, total and CTA). */}
                  {activeExtra && (
                    <ExtraServiceCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1924:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1924" data-component-end-line="1953" data-component-file="BookingFlowInteractive.tsx" data-component-name="ExtraServiceCard" data-component-content="%7B%22elementName%22%3A%22ExtraServiceCard%22%7D"
                      // Remount per extra. Without this, opening extra B
                      // after extra A reuses the instance and its inner AI
                      // verification panel keeps A's uploaded photo/preview
                      // state — B would appear pre-verified.
                      key={activeExtra.uid}
                      inSheet
                      index={extraServiceEntries.findIndex((e) => e.uid === activeExtra.uid)}
                      entry={activeExtra}
                      onChange={(patch) => updateExtraService(activeExtra.uid, patch)}
                      onRemove={() => {
                        setVerifiedMap((m) => {
                          const next = { ...m };
                          delete next[activeExtra.uid];
                          return next;
                        });
                        setAiVerificationMap((m) => {
                          const next = { ...m };
                          delete next[activeExtra.uid];
                          return next;
                        });
                        removeExtraService(activeExtra.uid);
                        setSheetTarget(null);
                      }}
                      verified={!!verifiedMap[activeExtra.uid]}
                      onVerifiedChange={(v, ai) => {
                        setVerifiedMap((m) => ({ ...m, [activeExtra.uid]: v }));
                        setAiVerificationMap((m) => ({ ...m, [activeExtra.uid]: ai }));
                      }}
                    />
                  )}

                  {!activeExtra && !serviceDetails && (
                    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1957:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1957" data-component-end-line="1957" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-12%20text-center%20text-%5B%2386868B%5D%20text-sm%22%2C%22textContent%22%3A%22Loading%20service%E2%80%A6%22%7D" className="py-12 text-center text-[#86868B] text-sm">Loading service…</div>
                  )}

                  {/* Measurement-mode selector — the customer chooses how to
                      give us measurements: type them by hand (Manual) or
                      upload a photo for AI confirmation (AI). The numbers are
                      entered the same way in both modes; AI just adds a
                      mandatory photo-verification gate below. */}
                  {!activeExtra && selectedCategoryType && serviceDetails && (
                    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1966:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1966" data-component-end-line="2009" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                      <MeasurementModeSelector data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:1967:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="1967" data-component-end-line="2008" data-component-file="BookingFlowInteractive.tsx" data-component-name="MeasurementModeSelector" data-component-content="%7B%22elementName%22%3A%22MeasurementModeSelector%22%7D"
                        mode={primaryMeasurementMode}
                        onModeChange={(next) => {
                          setPrimaryMeasurementMode(next);
                          // Either direction resets verification + photo so the
                          // two modes never share state (a stale "verified"
                          // flag could otherwise let Continue through after the
                          // measurements changed).
                          setVerifiedMap((m) => ({ ...m, primary: false }));
                          setVerificationComplete(false);
                          setAiVerificationData(null);
                          setAiVerificationMap((m) => ({ ...m, primary: null }));
                          if (next === 'manual') {
                            // Leaving AI → drop only the photo; keep any typed
                            // numbers since the manual inputs (about to show)
                            // are the source of truth in this mode.
                            setServiceMeasurements((prev) => ({ ...prev, images: [] }));
                          } else {
                            // Entering AI → the photo is the SOLE source of
                            // truth, so wipe every typed measurement. Critically
                            // this drops the carpetItems[]/curtainItems[] arrays
                            // the manual editor writes — the normalizer prefers
                            // those over the AI's legacy scalar fill, so leaving
                            // them would silently override the AI reading. Only
                            // the unit preference is kept.
                            setServiceMeasurements((prev) => ({
                              measurementUnit: prev.measurementUnit,
                              images: [],
                              ...(selectedCategoryType === 'carpet'
                                ? {
                                    carpetItems: [{ label: '', width: 0, length: 0, quantity: 1 }],
                                  }
                                : {}),
                              ...(selectedCategoryType === 'curtain'
                                ? {
                                    curtainItems: [{ label: '', width: 0, length: 0, quantity: 1 }],
                                  }
                                : {}),
                            }));
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Manual measurement entry — shown ONLY in 'manual' mode.
                      In 'ai' mode the AI photo result is the sole source of
                      truth, so the typed inputs are hidden entirely and the
                      AI panel below fills the numbers. */}
                  {!activeExtra &&
                    selectedCategoryType &&
                    serviceDetails &&
                    primaryMeasurementMode === 'manual' && (
                      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2020:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2020" data-component-end-line="2061" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                        {selectedCategoryType === 'sofa' ? (
                          <SofaSeatSelectionStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2022:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2022" data-component-end-line="2029" data-component-file="BookingFlowInteractive.tsx" data-component-name="SofaSeatSelectionStep" data-component-content="%7B%22elementName%22%3A%22SofaSeatSelectionStep%22%7D"
                            serviceName={serviceDetails.name}
                            pricePerSeat={serviceDetails.basePrice}
                            seats={serviceMeasurements.numberOfSeats || 0}
                            onSeatsChange={(n) =>
                              setServiceMeasurements((prev) => ({ ...prev, numberOfSeats: n }))
                            }
                          />
                        ) : selectedCategoryType === 'upholstery' ? (
                          <BedSelectionStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2031:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2031" data-component-end-line="2050" data-component-file="BookingFlowInteractive.tsx" data-component-name="BedSelectionStep" data-component-content="%7B%22elementName%22%3A%22BedSelectionStep%22%7D"
                            serviceName={serviceDetails.name}
                            pricePerItem={serviceDetails.basePrice}
                            quantity={serviceMeasurements.quantity || 0}
                            onQuantityChange={(n) =>
                              setServiceMeasurements((prev) => ({ ...prev, quantity: n }))
                            }
                            noun={
                              serviceDetails.name.toLowerCase().includes('cushion')
                                ? 'cushion'
                                : isChairService(serviceDetails.name)
                                  ? 'chair'
                                  : 'bed'
                            }
                            quickRange={
                              isChairService(serviceDetails.name)
                                ? UPHOLSTERY_QUICK_CHAIR
                                : UPHOLSTERY_QUICK_DEFAULT
                            }
                          />
                        ) : (
                          <ServiceMeasurementStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2052:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2052" data-component-end-line="2059" data-component-file="BookingFlowInteractive.tsx" data-component-name="ServiceMeasurementStep" data-component-content="%7B%22elementName%22%3A%22ServiceMeasurementStep%22%7D"
                            serviceType={selectedCategoryType}
                            serviceName={serviceDetails.name}
                            basePrice={serviceDetails.basePrice}
                            unit={serviceDetails.unit}
                            measurements={serviceMeasurements}
                            onMeasurementChange={setServiceMeasurements}
                          />
                        )}
                      </div>
                    )}

                  {/* AI photo measurement — only in 'ai' mode. The AI is the
                      sole source of truth here: the customer uploads a photo,
                      the AI extracts the measurements (filling them from
                      scratch — manual inputs are hidden in this mode), and
                      verifiedMap.primary gates Continue. No auto-advance —
                      the customer may still be stacking extras. */}
                  {!activeExtra &&
                    selectedCategoryType &&
                    serviceDetails &&
                    primaryMeasurementMode === 'ai' && (
                      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2074:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2074" data-component-end-line="2095" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-2%22%7D" className="pt-2">
                        <MeasurementVerificationStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2075:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2075" data-component-end-line="2094" data-component-file="BookingFlowInteractive.tsx" data-component-name="MeasurementVerificationStep" data-component-content="%7B%22elementName%22%3A%22MeasurementVerificationStep%22%7D"
                          embedded
                          aiSourceOfTruth
                          serviceType={selectedCategoryType}
                          serviceName={serviceDetails.name}
                          userMeasurements={serviceMeasurements}
                          aiItemTypeOverride={resolveAiItemType(serviceDetails.name)}
                          onImagesChange={(images) => {
                            setServiceMeasurements((prev) => ({ ...prev, images }));
                          }}
                          onMeasurementChange={(measurements) => {
                            setServiceMeasurements(measurements);
                          }}
                          onVerificationComplete={(verified, _aiResults, ai) => {
                            setVerifiedMap((m) => ({ ...m, primary: !!verified }));
                            setVerificationComplete(!!verified);
                            if (ai) setAiVerificationData(ai);
                            setAiVerificationMap((m) => ({ ...m, primary: ai || null }));
                          }}
                        />
                      </div>
                    )}
                </ServiceConfigSheet>

                {/* Multi-service stack — once the primary service has a
                    measurement, the customer can stack additional services
                    onto the same booking (same date / slot / address). Each
                    extra has its own service picker + measurement panel and
                    feeds into `extraServices` on the booking POST, exactly
                    like the admin manual booking form. */}
                {selectedCategoryType && serviceDetails && (
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2106:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2106" data-component-end-line="2293" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-6%20border-t-2%20border-dashed%20border-gray-200%20space-y-6%22%7D" className="pt-6 border-t-2 border-dashed border-gray-200 space-y-6">
                    {/* Cross-sell strip (Mockup 2) — replaces the old
                        text-only "Additional services" header. Visual
                        mini-cards for the rest of the catalogue with
                        one-tap Add; taps pre-seed an extra-service entry
                        so its card opens straight on the measurement UI.
                        The "one visit, no extra charges" framing is the
                        strongest attach argument in home services. */}
                    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2114:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2114" data-component-end-line="2179" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2115:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2115" data-component-end-line="2135" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-3%20flex-wrap%22%7D" className="flex items-start justify-between gap-3 flex-wrap">
                        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2116:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2116" data-component-end-line="2126" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                          <h3 data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2117:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2117" data-component-end-line="2122" data-component-file="BookingFlowInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Complete%20your%20home%22%7D" className="text-lg font-semibold text-[#1D1D1F]">
                            Complete your home{' '}
                            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2119:28" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2119" data-component-end-line="2121" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22%E2%80%94%20one%20visit%20covers%20it%20all%22%7D" className="text-sm font-normal text-[#86868B]">
                              — one visit covers it all
                            </span>
                          </h3>
                          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2123:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2123" data-component-end-line="2125" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%20mt-0.5%22%2C%22textContent%22%3A%22Your%20technician%20is%20already%20coming.%20Add%20now%20%E2%80%94%20zero%20extra%20visit%20charges.%22%7D" className="text-sm text-[#86868B] mt-0.5">
                            Your technician is already coming. Add now — zero extra visit charges.
                          </p>
                        </div>
                        <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2127:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2127" data-component-end-line="2134" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%23204653%5D%20hover%3Aunderline%20whitespace-nowrap%22%2C%22textContent%22%3A%22Browse%20all%20%2B%22%7D"
                          type="button"
                          onClick={addExtraService}
                          data-testid="browse-all-services"
                          className="text-sm font-semibold text-[#204653] hover:underline whitespace-nowrap"
                        >
                          Browse all +
                        </button>
                      </div>

                      {crossSellItems.length > 0 && (
                        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2138:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2138" data-component-end-line="2177" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20sm%3Agrid-cols-3%20gap-3%20mt-4%22%7D" className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                          {crossSellItems.map((item) => (
                            <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2140:28" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2140" data-component-end-line="2175" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22group%20text-left%20rounded-2xl%20border-%5B1.5px%5D%20border-black%2F%5B0.06%5D%20bg-white%20p-3%20hover%3Aborder-%5B%23204653%5D%2F40%20hover%3Ashadow-%5B0_6px_20px_rgba(0%2C0%2C0%2C0.06)%5D%20transition-all%20duration-200%22%7D"
                              key={item._id}
                              type="button"
                              onClick={() => addCrossSellService(item)}
                              data-testid={`cross-sell-${item.slug || item._id}`}
                              className="group text-left rounded-2xl border-[1.5px] border-black/[0.06] bg-white p-3 hover:border-[#204653]/40 hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all duration-200"
                            >
                              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2147:30" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2147" data-component-end-line="2161" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20rounded-xl%20bg-%5B%23F5F5F7%5D%20mb-2%20overflow-hidden%20flex%20items-center%20justify-center%22%7D" className="h-16 rounded-xl bg-[#F5F5F7] mb-2 overflow-hidden flex items-center justify-center">
                                {item.image ? (
                                  <AppImage data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2149:34" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2149" data-component-end-line="2153" data-component-file="BookingFlowInteractive.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
                                    src={item.image}
                                    alt={cleanServiceTitle(item.name)}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <Icon data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2155:34" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2155" data-component-end-line="2159" data-component-file="BookingFlowInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-%5B%23204653%5D%22%7D"
                                    name={(item.icon as any) || 'SparklesIcon'}
                                    size={24}
                                    className="text-[#204653]"
                                  />
                                )}
                              </div>
                              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2162:30" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2162" data-component-end-line="2164" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%231D1D1F%5D%20leading-tight%20line-clamp-2%22%7D" className="text-sm font-semibold text-[#1D1D1F] leading-tight line-clamp-2">
                                {cleanServiceTitle(item.name)}
                              </div>
                              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2165:30" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2165" data-component-end-line="2171" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mt-0.5%22%7D" className="text-xs text-[#86868B] mt-0.5">
                                {item.startingPrice
                                  ? `From ${item.startingPrice}`
                                  : item.basePrice
                                    ? `From ₹${Number(item.basePrice).toLocaleString('en-IN')}`
                                    : ''}
                              </div>
                              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2172:30" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2172" data-component-end-line="2174" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20inline-flex%20items-center%20gap-1%20px-3%20py-1%20rounded-full%20border-%5B1.5px%5D%20border-%5B%23204653%5D%20text-%5B%23204653%5D%20text-xs%20font-semibold%20group-hover%3Abg-%5B%23204653%5D%20group-hover%3Atext-white%20transition-colors%22%2C%22textContent%22%3A%22%2B%20Add%22%7D" className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full border-[1.5px] border-[#204653] text-[#204653] text-xs font-semibold group-hover:bg-[#204653] group-hover:text-white transition-colors">
                                + Add
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {extraServiceEntries.length > 0 && (
                      <>
                        {/* Added extras — compact summary strips, identical
                            in shape to the primary service's strip above.
                            The measurement UI itself lives in the sheet for
                            EVERY service now, so nothing configures inline
                            down the page anymore. */}
                        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2188:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2188" data-component-end-line="2270" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
                          {extraServiceEntries.map((extra) => {
                            const valid =
                              !!extra.categoryType &&
                              !!extra.details &&
                              measurementValidFor(extra.categoryType, extra.serviceMeasurements);
                            const total = extra.details
                              ? lineTotalFor(
                                  extra.categoryType,
                                  extra.serviceMeasurements,
                                  extra.details.basePrice
                                )
                              : 0;
                            return (
                              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2202:30" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2202" data-component-end-line="2267" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border-%5B1.5px%5D%20border-%5B%23204653%5D%2F20%20bg-white%20p-4%20md%3Ap-5%20flex%20items-center%20justify-between%20gap-4%20shadow-%5B0_2px_12px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                                key={extra.uid}
                                id={`extra-card-${extra.uid}`}
                                className="rounded-2xl border-[1.5px] border-[#204653]/20 bg-white p-4 md:p-5 flex items-center justify-between gap-4 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
                              >
                                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2207:32" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2207" data-component-end-line="2230" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
                                  <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2208:34" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2208" data-component-end-line="2210" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-semibold%20text-%5B%231D1D1F%5D%20truncate%22%7D" className="font-semibold text-[#1D1D1F] truncate">
                                    {cleanServiceTitle(extra.details?.name) || 'Pick a service'}
                                  </p>
                                  {valid ? (
                                    <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2212:36" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2212" data-component-end-line="2224" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%20mt-0.5%22%2C%22textContent%22%3A%22%C2%B7%22%7D" className="text-sm text-[#86868B] mt-0.5">
                                      {qtySummaryFor(extra.categoryType, extra.serviceMeasurements)}{' '}
                                      ·{' '}
                                      <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2215:38" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2215" data-component-end-line="2217" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-%5B%23204653%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="font-semibold text-[#204653]">
                                        ₹{total.toLocaleString('en-IN')}
                                      </span>
                                      {extra.measurementMode === 'ai' &&
                                        !verifiedMap[extra.uid] && (
                                          <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2220:42" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2220" data-component-end-line="2222" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-amber-700%22%2C%22textContent%22%3A%22%C2%B7%20AI%20verification%20pending%22%7D" className="ml-2 text-amber-700">
                                            · AI verification pending
                                          </span>
                                        )}
                                    </p>
                                  ) : (
                                    <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2226:36" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2226" data-component-end-line="2228" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-amber-700%20mt-0.5%22%2C%22textContent%22%3A%22Not%20set%20up%20yet%20%E2%80%94%20tap%20Set%20up%20to%20add%20quantity%22%7D" className="text-sm text-amber-700 mt-0.5">
                                      Not set up yet — tap Set up to add quantity
                                    </p>
                                  )}
                                </div>
                                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2231:32" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2231" data-component-end-line="2266" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20flex%20items-center%20gap-2%22%7D" className="flex-shrink-0 flex items-center gap-2">
                                  <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2232:34" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2232" data-component-end-line="2241" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22px-5%20py-2.5%20rounded-full%20border-2%20border-%5B%23204653%5D%20text-%5B%23204653%5D%20text-sm%20font-semibold%20hover%3Abg-%5B%23E4EDF0%5D%20transition-colors%22%7D"
                                    type="button"
                                    onClick={() =>
                                      setSheetTarget({ kind: 'extra', uid: extra.uid })
                                    }
                                    className="px-5 py-2.5 rounded-full border-2 border-[#204653] text-[#204653] text-sm font-semibold hover:bg-[#E4EDF0] transition-colors"
                                    data-testid={`edit-extra-${extra.uid}`}
                                  >
                                    {valid ? 'Edit' : 'Set up'}
                                  </button>
                                  <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2242:34" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2242" data-component-end-line="2265" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-9%20h-9%20rounded-full%20bg-%5B%23F5F5F7%5D%20hover%3Abg-red-50%20hover%3Atext-red-600%20flex%20items-center%20justify-center%20text-%5B%2386868B%5D%20transition-colors%22%7D"
                                    type="button"
                                    onClick={() => {
                                      // Drop this extra's verification state along
                                      // with the line so a removed AI extra can't
                                      // leave a stale verifiedMap entry behind.
                                      setVerifiedMap((m) => {
                                        const next = { ...m };
                                        delete next[extra.uid];
                                        return next;
                                      });
                                      setAiVerificationMap((m) => {
                                        const next = { ...m };
                                        delete next[extra.uid];
                                        return next;
                                      });
                                      removeExtraService(extra.uid);
                                    }}
                                    aria-label={`Remove ${cleanServiceTitle(extra.details?.name) || 'service'}`}
                                    className="w-9 h-9 rounded-full bg-[#F5F5F7] hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-[#86868B] transition-colors"
                                    data-testid={`remove-extra-${extra.uid}`}
                                  >
                                    <Icon data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2264:36" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2264" data-component-end-line="2264" data-component-file="BookingFlowInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={16} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Bottom "+ Add another service" — mirrors the
                            header button so customers don't have to scroll
                            back up after filling out the last card. */}
                        <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2275:24" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2275" data-component-end-line="2290" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-full%20inline-flex%20items-center%20justify-center%20gap-1.5%20px-4%20py-3%20rounded-2xl%20border-2%20border-dashed%20border-%5B%23204653%5D%2F30%20text-%5B%23204653%5D%20text-sm%20font-semibold%20hover%3Aborder-%5B%23204653%5D%20hover%3Abg-%5B%23204653%5D%2F5%20transition-smooth%22%2C%22textContent%22%3A%22Add%20another%20service%22%7D"
                          type="button"
                          onClick={addExtraService}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl border-2 border-dashed border-[#204653]/30 text-[#204653] text-sm font-semibold hover:border-[#204653] hover:bg-[#204653]/5 transition-smooth"
                          data-testid="add-another-service-bottom"
                        >
                          <svg data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2281:26" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2281" data-component-end-line="2288" data-component-file="BookingFlowInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2282:28" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2282" data-component-end-line="2287" data-component-file="BookingFlowInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                              d="M8 1v14M1 8h14"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          Add another service
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ────────────────────────────────────────────────────────
                STEP 2 (Add-ons) IS CURRENTLY SKIPPED. The customer flow
                jumps from Step 1 (service + measurements) directly to
                Step 3 (verification) via handleProceed's special case.
                The full render block is preserved here (commented) so
                restoring Add-ons is a copy-uncomment away.
                ────────────────────────────────────────────────────────
            {currentStep === 2 && (
              selectedPackage ? (
                <div className="space-y-10">
                  // Add-ons — one labeled block per service (primary + every
                  // extra picked on Step 1). Each block fetches its own
                  // service's add-ons array and toggles into the single
                  // shared `selectedAddOns` list.
                  <ServiceAddOnsBlock
                    packageId={selectedPackage}
                    serviceName={serviceDetails?.name || ''}
                    categoryType={selectedCategoryType}
                    selectedAddOns={selectedAddOns}
                    onAddOnToggle={handleAddOnToggle}
                    isPrimary
                  />

                  {extraServiceEntries
                    .filter((e) => e.packageId)
                    .map((extra) => (
                      <ServiceAddOnsBlock
                        key={extra.uid}
                        packageId={extra.packageId}
                        serviceName={extra.details?.name || ''}
                        categoryType={extra.categoryType || ''}
                        selectedAddOns={selectedAddOns}
                        onAddOnToggle={handleAddOnToggle}
                      />
                    ))}

                  // Cross-sell ("Add More Items") — global to the booking,
                  // so it only renders once. ServiceSelectionStep with
                  // hideAddOns leaves only the cross-sell visible.
                  <ServiceSelectionStep
                    selectedService={selectedService}
                    selectedPackage={selectedPackage}
                    selectedAddOns={selectedAddOns}
                    furnitureItems={furnitureItems}
                    onServiceChange={(serviceId, categoryType) => {
                      setSelectedService(serviceId);
                      if (categoryType) {
                        setSelectedCategoryType(categoryType as 'sofa' | 'curtain' | 'carpet' | 'upholstery');
                      }
                    }}
                    onPackageChange={setSelectedPackage}
                    onAddOnToggle={handleAddOnToggle}
                    onFurnitureQuantityChange={handleFurnitureQuantityChange}
                    hideServicePickers
                    hideAddOns
                  />
                </div>
              ) : (
                <div className="space-y-8">
                  <div className="animate-pulse space-y-6">
                    <div className="h-12 bg-gray-200 rounded-2xl w-1/3" />
                    <div className="h-64 bg-gray-200 rounded-2xl" />
                  </div>
                </div>
              )
            )}
                ──────────────────────────────────────────────────────── */}

            {/* The standalone Step 3 ("Verify Measurements") has been
                retired. AI photo verification is now an inline, opt-in
                measurement MODE within Step 1 — handled above for the
                primary service and inside each ExtraServiceCard for
                additional services. currentStep can never be 3 now;
                the flow goes 1 → 4 (Schedule) directly. */}

            {currentStep === 4 && (
              <ScheduleSelectionStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2374:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2374" data-component-end-line="2387" data-component-file="BookingFlowInteractive.tsx" data-component-name="ScheduleSelectionStep" data-component-content="%7B%22elementName%22%3A%22ScheduleSelectionStep%22%7D"
                serviceId={selectedPackage}
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                orderValue={pricing.total}
                surgeEnabled={surgeEnabled}
                onDateChange={(date) => {
                  setSelectedDate(date);
                  setSelectedSlot('');
                }}
                onSlotChange={(slotId, startTime, endTime, workerId) => {
                  setSelectedSlot(slotId);
                }}
              />
            )}

            {currentStep === 5 && (
              <AddressDetailsStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2391:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2391" data-component-end-line="2391" data-component-file="BookingFlowInteractive.tsx" data-component-name="AddressDetailsStep" data-component-content="%7B%22elementName%22%3A%22AddressDetailsStep%22%7D" addressData={addressData} onAddressChange={setAddressData} />
            )}

            {currentStep === 6 && (
              <PaymentStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2395:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2395" data-component-end-line="2421" data-component-file="BookingFlowInteractive.tsx" data-component-name="PaymentStep" data-component-content="%7B%22elementName%22%3A%22PaymentStep%22%7D"
                totalAmount={pricing.total}
                // Plan-INDEPENDENT Cash-on-Delivery total for the COD card's
                // preview: services (no 10% online discount) + surge + the
                // travel fee (charged on COD even while the currently
                // selected plan waives it). Without this the unselected COD
                // card showed the online-discounted number (₹3,135 instead
                // of ₹3,550) — a mislead the customer only discovered after
                // switching.
                codTotal={
                  (pricing.servicesTotal || 0) +
                  (pricing.surgeFee || 0) +
                  (pricing.travelFee || pricing.travelWaivedAmount || 0)
                }
                paymentPlan={paymentPlan}
                onPaymentPlanChange={setPaymentPlan}
                onPreparePayment={createBookingDraft}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                onProcessingChange={setIsPaymentProcessing}
                registerPayHandler={registerPayHandler}
                customerDetails={{
                  fullName: addressData.fullName || user?.name || '',
                  email: addressData.email || user?.email || '',
                  phone: addressData.phone || user?.phone || '',
                }}
              />
            )}
          </motion.div>

          {/* Mobile-only: the FULL desktop-style price breakdown on the
              payment screen. On desktop the sticky sidebar already shows
              every line; on mobile the sidebar is hidden and the bottom bar
              shows only the total — so at payment time we render the same
              detailed receipt (services, add-ons, extras, travel, surge,
              discounts, GST) inline. The bottom margin clears the fixed
              bottom bar so the Total line is never hidden under it. */}
          {currentStep === 6 && pricing.hasServices && (
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2433:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2433" data-component-end-line="2435" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20mb-28%22%7D" className="lg:hidden mb-28">
              <PriceBreakdownCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2434:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2434" data-component-end-line="2434" data-component-file="BookingFlowInteractive.tsx" data-component-name="PriceBreakdownCard" data-component-content="%7B%22elementName%22%3A%22PriceBreakdownCard%22%7D" pricing={pricing} />
            </div>
          )}

          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2438:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2438" data-component-end-line="2447" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ablock%20hidden%22%7D" className="lg:block hidden">
            <PricingSidebar data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2439:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2439" data-component-end-line="2446" data-component-file="BookingFlowInteractive.tsx" data-component-name="PricingSidebar" data-component-content="%7B%22elementName%22%3A%22PricingSidebar%22%7D"
              pricing={pricing}
              currentStep={currentStep}
              onProceed={currentStep === 6 ? triggerPayment : handleProceed}
              onBack={handleBack}
              isValid={!!isStepValid() && !isPaymentProcessing}
              isProcessing={isPaymentProcessing}
            />
          </div>
        </div>

        {/* Mobile Bottom Bar - Premium Design */}
        {/* Compact by design: this bar is fixed over the page, so every pixel
            it takes is a pixel of the form the customer can't reach. It used
            to stand 229px tall (29% of an 800px phone screen), which buried
            the "Pin Location on Map" button underneath it at maximum scroll —
            the page looked frozen with no way forward. Tight paddings plus a
            single total row keep it near ~120px. */}
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2457:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2457" data-component-end-line="2543" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20fixed%20bottom-0%20left-0%20right-0%20bg-white%2F95%20backdrop-blur-xl%20border-t%20border-gray-100%20shadow-%5B0_-4px_30px_rgb(0%2C0%2C0%2C0.08)%5D%20px-4%20pt-2.5%20pb-3%20safe-area-inset-bottom%20z-50%22%7D" className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-[0_-4px_30px_rgb(0,0,0,0.08)] px-4 pt-2.5 pb-3 safe-area-inset-bottom z-50">
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2458:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2458" data-component-end-line="2493" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-2%22%7D" className="flex items-center justify-between mb-2">
            {pricing.hasServices ? (
              <>
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2461:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2461" data-component-end-line="2477" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2462:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2462" data-component-end-line="2462" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B13px%5D%20font-medium%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Total%20Amount%22%7D" className="text-[13px] font-medium text-[#86868B]">Total Amount</span>
                  {pricing.surgeFee > 0 && (
                    <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2464:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2464" data-component-end-line="2466" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-%5B11px%5D%20font-medium%20text-amber-600%20bg-amber-50%20px-2%20py-0.5%20rounded-full%22%2C%22textContent%22%3A%22incl.%20%E2%82%B9%20surge%22%7D" className="ml-2 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      incl. ₹{pricing.surgeFee} surge
                    </span>
                  )}
                  {/* Uniform receipt on mobile too: carted-but-unmeasured
                      services can't be priced yet, so say they're excluded
                      instead of silently leaving the total unchanged. */}
                  {(pricing.extraServiceLines || []).some((l) => !(l.cost > 0)) && (
                    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2472:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2472" data-component-end-line="2475" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mt-0.5%22%2C%22textContent%22%3A%22added%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%E2%80%94%20enter%20size%20to%20include%20in%20price%22%7D" className="text-xs text-[#86868B] mt-0.5">
                      {(pricing.extraServiceLines || []).filter((l) => !(l.cost > 0)).length} added
                      — enter size to include in price
                    </div>
                  )}
                </div>
                <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2478:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2478" data-component-end-line="2480" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xl%20font-bold%20text-%5B%231D1D1F%5D%20font-heading%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-xl font-bold text-[#1D1D1F] font-heading">
                  ₹{(pricing.displayTotal ?? pricing.total).toLocaleString('en-IN')}
                </span>
              </>
            ) : (
              // Empty state — before anything is configured there is no
              // honest number to show. The old bar showed the bare ₹500
              // travel fee here, which read as "you owe ₹500 for nothing".
              <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2486:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2486" data-component-end-line="2491" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%22%7D" className="text-sm text-[#86868B]">
                {pricing.pendingPrimaryName ||
                (pricing.extraServiceLines || []).some((l) => !(l.cost > 0))
                  ? 'Almost there — add size or quantity to see your price.'
                  : 'Select a service to see your price — GST included, no hidden charges.'}
              </span>
            )}
          </div>
          {/* The #1 "why is the button grey?" case: address step needs the
              pinned location SAVED. Say so instead of leaving users stuck. */}
          {currentStep === 5 && !isStepValid() && (
            <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2497:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2497" data-component-end-line="2503" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mb-2%20-mt-0.5%20text-%5B11.5px%5D%20leading-snug%20font-medium%20text-amber-700%20bg-amber-50%20rounded-lg%20px-2.5%20py-1.5%22%7D" className="mb-2 -mt-0.5 text-[11.5px] leading-snug font-medium text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
              {!addressData.latitude
                ? 'Fill your address, then tap "Pin Location on Map".'
                : !addressData.locationSaved
                  ? 'Almost done — tap "Save Location & Continue".'
                  : 'Complete the highlighted address fields.'}
            </p>
          )}
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2505:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2505" data-component-end-line="2542" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2.5%22%7D" className="flex gap-2.5">
            {/* Back is secondary — give it just enough width so the primary
                action never wraps onto two lines (which is what made this
                bar so tall). */}
            {currentStep > 1 && (
              <motion.button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2510:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2510" data-component-end-line="2518" data-component-file="BookingFlowInteractive.tsx" data-component-name="motion.button" data-component-content="%7B%22elementName%22%3A%22motion.button%22%2C%22className%22%3A%22shrink-0%20px-5%20py-3%20rounded-full%20border-2%20border-gray-200%20bg-white%20text-%5B%231D1D1F%5D%20text-%5B14px%5D%20font-semibold%20hover%3Aborder-%5B%23204653%5D%20hover%3Abg-%5B%23E4EDF0%5D%20transition-all%20duration-200%22%2C%22textContent%22%3A%22Back%22%7D"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBack}
                className="shrink-0 px-5 py-3 rounded-full border-2 border-gray-200 bg-white text-[#1D1D1F] text-[14px] font-semibold hover:border-[#204653] hover:bg-[#E4EDF0] transition-all duration-200"
                data-testid="booking-back-btn"
              >
                Back
              </motion.button>
            )}
            <motion.button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2520:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2520" data-component-end-line="2541" data-component-file="BookingFlowInteractive.tsx" data-component-name="motion.button" data-component-content="%7B%22elementName%22%3A%22motion.button%22%7D"
              whileHover={{ scale: isStepValid() && !isPaymentProcessing ? 1.02 : 1 }}
              whileTap={{ scale: isStepValid() && !isPaymentProcessing ? 0.98 : 1 }}
              // On the payment step this button pays (same charge as the
              // in-page "Pay" button); everywhere else it advances the flow.
              onClick={currentStep === 6 ? triggerPayment : handleProceed}
              disabled={!isStepValid() || isPaymentProcessing}
              className={`flex-1 min-w-0 px-4 py-3 rounded-full text-[14px] font-semibold whitespace-nowrap transition-all duration-200 ${
                isStepValid() && !isPaymentProcessing
                  ? 'bg-[#204653] text-white hover:bg-[#1a3a44] shadow-[0_4px_20px_rgb(74,124,126,0.3)]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="booking-continue-btn"
            >
              {isPaymentProcessing
                ? 'Processing...'
                : currentStep === 5
                  ? 'Proceed to Payment'
                  : currentStep === 6
                    ? 'Pay Now'
                    : 'Continue'}
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlowInteractive;

/**
 * Two-option chooser shown in Step 1 (and inside each ExtraServiceCard)
 * between picking a service and entering measurements. Lets the customer
 * decide HOW to give measurements:
 *   - Manual: type the numbers and continue (fastest, default).
 *   - AI: type the numbers AND upload a photo so the AI confirms them
 *     (adds a mandatory verification gate before Continue).
 * Pure presentational toggle — the parent owns the mode state and the
 * verification wiring. Shared by primary + extras so both read identically.
 */
const MeasurementModeSelector = ({
  mode,
  onModeChange,
}: {
  mode: 'manual' | 'ai';
  onModeChange: (next: 'manual' | 'ai') => void;
}) => {
  const options: Array<{
    key: 'manual' | 'ai';
    title: string;
    desc: string;
    icon: string;
  }> = [
    {
      key: 'manual',
      title: 'Enter manually',
      desc: 'Type your measurements yourself — quickest way to continue.',
      icon: 'PencilSquareIcon',
    },
    {
      key: 'ai',
      title: 'Measure with AI photo',
      desc: 'Upload a photo and our AI measures it for you — no manual entry needed.',
      icon: 'SparklesIcon',
    },
  ];
  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2588:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2588" data-component-end-line="2632" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2589:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2589" data-component-end-line="2591" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%231D1D1F%5D%20mb-2%22%2C%22textContent%22%3A%22How%20would%20you%20like%20to%20provide%20measurements%3F%22%7D" className="text-sm font-semibold text-[#1D1D1F] mb-2">
        How would you like to provide measurements?
      </div>
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2592:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2592" data-component-end-line="2631" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20gap-3%22%7D" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const active = mode === opt.key;
          return (
            <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2596:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2596" data-component-end-line="2628" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
              key={opt.key}
              type="button"
              onClick={() => onModeChange(opt.key)}
              aria-pressed={active}
              data-testid={`measurement-mode-${opt.key}`}
              className={`text-left p-4 rounded-2xl border-2 transition-all ${
                active
                  ? 'border-[#204653] bg-[#F5FAF9] shadow-[0_4px_15px_rgb(32,70,83,0.12)]'
                  : 'border-gray-200 bg-white hover:border-[#204653]/40'
              }`}
            >
              <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2608:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2608" data-component-end-line="2627" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2609:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2609" data-component-end-line="2615" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    active ? 'bg-[#204653] text-white' : 'bg-gray-100 text-[#86868B]'
                  }`}
                >
                  <Icon data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2614:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2614" data-component-end-line="2614" data-component-file="BookingFlowInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={opt.icon as any} size={18} />
                </div>
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2616:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2616" data-component-end-line="2626" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2617:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2617" data-component-end-line="2624" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                    <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2618:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2618" data-component-end-line="2618" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-%5B%231D1D1F%5D%22%7D" className="font-semibold text-[#1D1D1F]">{opt.title}</span>
                    {active && (
                      <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2620:22" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2620" data-component-end-line="2622" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20uppercase%20tracking-wider%20px-1.5%20py-0.5%20rounded-full%20bg-%5B%23204653%5D%20text-white%22%2C%22textContent%22%3A%22Selected%22%7D" className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#204653] text-white">
                        Selected
                      </span>
                    )}
                  </div>
                  <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2625:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2625" data-component-end-line="2625" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mt-0.5%22%7D" className="text-xs text-[#86868B] mt-0.5">{opt.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Inline card that lets the customer pick another service to stack onto the
 * same appointment. Mirrors the admin manual booking's "Additional Services"
 * pattern: a single flat Service dropdown, then a rich stepper / area card
 * with a live Total + Quick-select chips, then a Line total at the bottom.
 * Category type is auto-derived from the GET /api/services/:id response
 * (which populates `category`) — no separate category picker.
 */
interface ExtraServiceCardProps {
  index: number;
  entry: {
    uid: string;
    serviceId: string;
    packageId: string;
    categoryType: 'sofa' | 'curtain' | 'carpet' | 'upholstery' | '';
    serviceMeasurements: any;
    details: { name: string; basePrice: number; unit: string } | null;
    measurementMode: 'manual' | 'ai';
  };
  onChange: (patch: Partial<ExtraServiceCardProps['entry']>) => void;
  onRemove: () => void;
  // AI verification state for THIS extra, owned by the parent's
  // verifiedMap/aiVerificationMap. `verified` gates the parent's Continue
  // button; `onVerifiedChange` writes both maps when the inline AI check
  // completes (or is reset when the customer switches back to manual).
  verified: boolean;
  onVerifiedChange: (verified: boolean, ai: any) => void;
  // Rendered inside ServiceConfigSheet rather than inline on the page. The
  // sheet already supplies the header, the running total and the CTA, so
  // this drops its own card chrome and duplicate total to avoid showing
  // the customer the same number twice.
  inSheet?: boolean;
}

const ExtraServiceCard = ({
  index,
  entry,
  onChange,
  onRemove,
  verified,
  onVerifiedChange,
  inSheet = false,
}: ExtraServiceCardProps) => {
  const [services, setServices] = useState<
    Array<{ _id: string; name: string; basePrice?: number; unit?: string }>
  >([]);

  // Load ALL active services flat — no separate category picker, exactly
  // like the admin manual booking form's "Additional Services" dropdown.
  // The category type is derived later by the parent's lazy-fetch effect
  // from the Service.type field on the GET /api/services/:id response.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/services?isActive=true');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setServices(Array.isArray(data) ? data : []);
      } catch {
        /* ignore — empty dropdown is acceptable fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Line total for this entry. Mirrors the parent's pricing math so the
  // customer sees the line cost update live as they adjust quantities or
  // dimensions, without waiting for the sidebar re-render.
  const lineTotal = (() => {
    const det = entry.details;
    const m = entry.serviceMeasurements;
    if (!det || !entry.categoryType) return 0;
    switch (entry.categoryType) {
      case 'sofa':
        return (m.numberOfSeats || 0) * det.basePrice;
      case 'upholstery':
        return (m.quantity || 0) * det.basePrice;
      case 'curtain':
        // Shared helper handles both curtainItems[] (multi-size) and the
        // legacy single-size fallback. Returns null when no usable rows.
        return curtainLineCost(m, det.basePrice) ?? 0;
      case 'carpet':
        // Same pattern, see carpetMeasurements.ts.
        return carpetLineCost(m, det.basePrice) ?? 0;
      default:
        return 0;
    }
  })();

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2730:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2730" data-component-end-line="2973" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={inSheet ? '' : 'rounded-2xl border-2 border-[#204653]/20 bg-[#F5FAF9] p-5'}>
      {!inSheet && (
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2732:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2732" data-component-end-line="2743" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-3%22%7D" className="flex items-center justify-between mb-3">
          <h4 data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2733:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2733" data-component-end-line="2735" data-component-file="BookingFlowInteractive.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22text-base%20font-semibold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22Additional%20service%20%23%22%7D" className="text-base font-semibold text-[#1D1D1F]">
            Additional service #{index + 1}
          </h4>
          <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2736:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2736" data-component-end-line="2742" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-red-600%20hover%3Aunderline%22%2C%22textContent%22%3A%22Remove%22%7D"
            type="button"
            onClick={onRemove}
            className="text-xs font-semibold text-red-600 hover:underline"
          >
            Remove
          </button>
        </div>
      )}

      {/* Service picker. In the sheet it only shows while nothing is picked
          yet (the "Add another service" path) — once a service is chosen the
          sheet header names it, so a dropdown repeating that name is noise.
          To swap services, close and tap a different card. */}
      {(!inSheet || !entry.packageId) && (
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2751:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2751" data-component-end-line="2777" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%22%7D" className="mb-4">
          <label data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2752:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2752" data-component-end-line="2752" data-component-file="BookingFlowInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-xs%20font-medium%20text-%5B%2386868B%5D%20mb-1%22%2C%22textContent%22%3A%22Service%20*%22%7D" className="block text-xs font-medium text-[#86868B] mb-1">Service *</label>
          <select data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2753:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2753" data-component-end-line="2776" data-component-file="BookingFlowInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border-2%20border-%5B%23204653%5D%20rounded-xl%20bg-white%20text-sm%20font-medium%20text-%5B%231D1D1F%5D%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%23204653%5D%20focus%3Aborder-%5B%23204653%5D%22%7D"
            value={entry.packageId}
            onChange={(e) =>
              // Clear category + measurements + details — they'll be re-derived
              // by the parent's lazy-fetch effect from the populated category
              // on the GET /api/services/:id response.
              onChange({
                packageId: e.target.value,
                serviceId: e.target.value,
                categoryType: '',
                serviceMeasurements: {},
                details: null,
              })
            }
            className="w-full px-4 py-3 border-2 border-[#204653] rounded-xl bg-white text-sm font-medium text-[#1D1D1F] focus:outline-none focus:ring-2 focus:ring-[#204653] focus:border-[#204653]"
          >
            <option data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2769:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2769" data-component-end-line="2769" data-component-file="BookingFlowInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22textContent%22%3A%22%E2%80%94%20pick%20a%20service%20%E2%80%94%22%7D" value="">— pick a service —</option>
            {services.map((s) => (
              <option data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2771:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2771" data-component-end-line="2774" data-component-file="BookingFlowInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%7D" key={s._id} value={s._id}>
                {s.name}
                {s.basePrice ? ` · base ₹${s.basePrice.toLocaleString('en-IN')}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Waiting on the parent's lazy details fetch — without this the sheet
          body would be blank between picking a service and its details
          landing, reading as a dead dropdown. */}
      {inSheet && entry.packageId && !entry.details && (
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2784:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2784" data-component-end-line="2784" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-12%20text-center%20text-%5B%2386868B%5D%20text-sm%22%2C%22textContent%22%3A%22Loading%20service%E2%80%A6%22%7D" className="py-12 text-center text-[#86868B] text-sm">Loading service…</div>
      )}

      {entry.categoryType && entry.details && (
        <>
          {/* Same Manual / AI measurement-mode choice as the primary
              service — keeps the two paths identical for every extra. */}
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2791:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2791" data-component-end-line="2832" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-2%22%7D" className="mb-2">
            <MeasurementModeSelector data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2792:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2792" data-component-end-line="2831" data-component-file="BookingFlowInteractive.tsx" data-component-name="MeasurementModeSelector" data-component-content="%7B%22elementName%22%3A%22MeasurementModeSelector%22%7D"
              mode={entry.measurementMode}
              onModeChange={(next) => {
                onChange({ measurementMode: next });
                // Reset this extra's verification in BOTH directions so the
                // two modes never share state.
                onVerifiedChange(false, null);
                if (next === 'manual') {
                  // Leaving AI → drop only the photo; keep typed numbers
                  // (the manual inputs about to show are authoritative).
                  onChange({
                    serviceMeasurements: {
                      ...entry.serviceMeasurements,
                      images: [],
                    },
                  });
                } else {
                  // Entering AI → photo is the sole source of truth, so wipe
                  // every typed measurement (incl. the carpetItems[]/
                  // curtainItems[] arrays the normalizer would prefer over
                  // the AI's scalar fill). Keep only the unit preference.
                  onChange({
                    serviceMeasurements: {
                      measurementUnit: entry.serviceMeasurements?.measurementUnit,
                      images: [],
                      ...(entry.categoryType === 'carpet'
                        ? {
                            carpetItems: [{ label: '', width: 0, length: 0, quantity: 1 }],
                          }
                        : {}),
                      ...(entry.categoryType === 'curtain'
                        ? {
                            curtainItems: [{ label: '', width: 0, length: 0, quantity: 1 }],
                          }
                        : {}),
                    },
                  });
                }
              }}
            />
          </div>

          {/* Manual measurement inputs — shown ONLY in 'manual' mode. In
              'ai' mode the AI photo result fills the numbers, so these are
              hidden and the AI panel below is the sole input. */}
          {entry.measurementMode === 'manual' && (
            <>
              {entry.categoryType === 'sofa' && (
                <ExtraStepperCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2840:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2840" data-component-end-line="2855" data-component-file="BookingFlowInteractive.tsx" data-component-name="ExtraStepperCard" data-component-content="%7B%22elementName%22%3A%22ExtraStepperCard%22%7D"
                  name={entry.details.name}
                  pricePerUnit={entry.details.basePrice}
                  unit="seat"
                  quantity={entry.serviceMeasurements.numberOfSeats || 0}
                  quickRange={[1, 2, 3, 4, 5, 6, 7, 8]}
                  maxQuantity={20}
                  onChange={(n) =>
                    onChange({
                      serviceMeasurements: {
                        ...entry.serviceMeasurements,
                        numberOfSeats: n,
                      },
                    })
                  }
                />
              )}

              {entry.categoryType === 'upholstery' && (
                <ExtraStepperCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2859:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2859" data-component-end-line="2884" data-component-file="BookingFlowInteractive.tsx" data-component-name="ExtraStepperCard" data-component-content="%7B%22elementName%22%3A%22ExtraStepperCard%22%7D"
                  name={entry.details.name}
                  pricePerUnit={entry.details.basePrice}
                  unit={
                    entry.details.name.toLowerCase().includes('cushion')
                      ? 'cushion'
                      : isChairService(entry.details.name)
                        ? 'chair'
                        : 'item'
                  }
                  quantity={entry.serviceMeasurements.quantity || 0}
                  quickRange={
                    isChairService(entry.details.name)
                      ? UPHOLSTERY_QUICK_CHAIR
                      : UPHOLSTERY_QUICK_DEFAULT
                  }
                  maxQuantity={50}
                  onChange={(n) =>
                    onChange({
                      serviceMeasurements: {
                        ...entry.serviceMeasurements,
                        quantity: n,
                      },
                    })
                  }
                />
              )}

              {entry.categoryType === 'curtain' && (
                <ExtraCurtainCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2888:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2888" data-component-end-line="2906" data-component-file="BookingFlowInteractive.tsx" data-component-name="ExtraCurtainCard" data-component-content="%7B%22elementName%22%3A%22ExtraCurtainCard%22%7D"
                  name={entry.details.name}
                  pricePerSqft={entry.details.basePrice}
                  measurements={entry.serviceMeasurements}
                  onChange={(nextMeas) =>
                    onChange({
                      serviceMeasurements: {
                        ...entry.serviceMeasurements,
                        ...nextMeas,
                        // Clear the legacy single-size fields whenever we
                        // write the new array shape so the persisted booking
                        // carries only one canonical representation.
                        curtainWidth: undefined,
                        curtainLength: undefined,
                        numberOfPanels: undefined,
                      },
                    })
                  }
                />
              )}

              {entry.categoryType === 'carpet' && (
                <ExtraCarpetCard data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2910:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2910" data-component-end-line="2928" data-component-file="BookingFlowInteractive.tsx" data-component-name="ExtraCarpetCard" data-component-content="%7B%22elementName%22%3A%22ExtraCarpetCard%22%7D"
                  name={entry.details.name}
                  pricePerSqft={entry.details.basePrice}
                  measurements={entry.serviceMeasurements}
                  onChange={(nextMeas) =>
                    onChange({
                      serviceMeasurements: {
                        ...entry.serviceMeasurements,
                        ...nextMeas,
                        // Clear the legacy single-size fields whenever we write
                        // the new array shape so the persisted booking carries
                        // only one canonical representation.
                        carpetWidth: undefined,
                        carpetLength: undefined,
                        numberOfCarpets: undefined,
                      },
                    })
                  }
                />
              )}
            </>
          )}

          {/* In the sheet the pinned footer already shows this total live. */}
          {!inSheet && lineTotal > 0 && (
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2935:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2935" data-component-end-line="2938" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%20pt-3%20border-t%20border-%5B%23204653%5D%2F15%20flex%20items-center%20justify-between%20text-sm%22%7D" className="mt-3 pt-3 border-t border-[#204653]/15 flex items-center justify-between text-sm">
              <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2936:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2936" data-component-end-line="2936" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%231D1D1F%5D%20font-medium%22%2C%22textContent%22%3A%22Line%20total%22%7D" className="text-[#1D1D1F] font-medium">Line total</span>
              <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2937:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2937" data-component-end-line="2937" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-bold%20text-%5B%23204653%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="font-bold text-[#204653]">₹{lineTotal.toLocaleString('en-IN')}</span>
            </div>
          )}

          {/* Inline AI verification for this extra — only in 'ai' mode.
              Mirrors the primary service's inline panel; verified state is
              lifted to the parent's verifiedMap via onVerifiedChange so the
              Continue gate accounts for every AI-mode service. */}
          {entry.measurementMode === 'ai' && (
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2946:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2946" data-component-end-line="2969" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%20pt-4%20border-t%20border-%5B%23204653%5D%2F15%22%7D" className="mt-4 pt-4 border-t border-[#204653]/15">
              <MeasurementVerificationStep data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:2947:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="2947" data-component-end-line="2968" data-component-file="BookingFlowInteractive.tsx" data-component-name="MeasurementVerificationStep" data-component-content="%7B%22elementName%22%3A%22MeasurementVerificationStep%22%7D"
                embedded
                aiSourceOfTruth
                serviceType={entry.categoryType as 'sofa' | 'curtain' | 'carpet' | 'upholstery'}
                serviceName={entry.details.name}
                userMeasurements={entry.serviceMeasurements}
                aiItemTypeOverride={resolveAiItemType(entry.details.name)}
                onImagesChange={(images) => {
                  onChange({
                    serviceMeasurements: {
                      ...entry.serviceMeasurements,
                      images,
                    },
                  });
                }}
                onMeasurementChange={(measurements) => {
                  onChange({ serviceMeasurements: measurements });
                }}
                onVerificationComplete={(isVerified, _aiResults, ai) => {
                  onVerifiedChange(!!isVerified, ai || null);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Rich stepper card matching the admin manual booking form's
// `StepperItemCard` visual style — left column has name + per-unit price,
// middle has a − N + stepper, right has live Total. A row of "Quick select"
// chips below jumps to common quantities.
const ExtraStepperCard = ({
  name,
  pricePerUnit,
  unit,
  quantity,
  quickRange,
  maxQuantity,
  onChange,
}: {
  name: string;
  pricePerUnit: number;
  // The singular label for one unit (seat / item / cushion / chair / bed).
  // Free string so upholstery variants like "chair" render correctly.
  unit: string;
  quantity: number;
  quickRange: number[];
  maxQuantity: number;
  onChange: (n: number) => void;
}) => {
  const clamp = (n: number) => Math.max(0, Math.min(n, maxQuantity));
  const total = pricePerUnit * quantity;
  const pluralize = (n: number, sing: string) => (n === 1 ? sing : `${sing}s`);
  const unitNoun = pluralize(quantity, unit);
  const chipNoun = (n: number) =>
    unit === 'seat' ? (n === 1 ? 'Seat' : 'Seats') : pluralize(n, unit);

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3008:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3008" data-component-end-line="3074" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border%20border-gray-200%20bg-white%20p-5%22%7D" className="rounded-2xl border border-gray-200 bg-white p-5">
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3009:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3009" data-component-end-line="3050" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-4%20flex-wrap%22%7D" className="flex items-start justify-between gap-4 flex-wrap">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3010:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3010" data-component-end-line="3016" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3011:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3011" data-component-end-line="3011" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-bold%20text-%5B%231D1D1F%5D%22%7D" className="text-lg font-bold text-[#1D1D1F]">{name}</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3012:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3012" data-component-end-line="3015" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-2xl font-bold text-[#1D1D1F] mt-2">
            ₹{pricePerUnit.toLocaleString('en-IN')}{' '}
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3014:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3014" data-component-end-line="3014" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22per%22%7D" className="text-sm font-normal text-[#86868B]">per {unit}</span>
          </p>
        </div>

        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3018:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3018" data-component-end-line="3044" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%22%7D" className="flex items-center gap-4">
          <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3019:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3019" data-component-end-line="3029" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20rounded-full%20bg-white%20border%20border-gray-300%20text-%5B%231D1D1F%5D%20hover%3Abg-gray-100%20flex%20items-center%20justify-center%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
            type="button"
            onClick={() => onChange(clamp(quantity - 1))}
            disabled={quantity === 0}
            className="w-10 h-10 rounded-full bg-white border border-gray-300 text-[#1D1D1F] hover:bg-gray-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Decrease"
          >
            <svg data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3026:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3026" data-component-end-line="3028" data-component-file="BookingFlowInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="16" height="2" viewBox="0 0 16 2">
              <rect data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3027:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3027" data-component-end-line="3027" data-component-file="BookingFlowInteractive.tsx" data-component-name="rect" data-component-content="%7B%22elementName%22%3A%22rect%22%7D" width="16" height="2" fill="currentColor" />
            </svg>
          </button>
          <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3030:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3030" data-component-end-line="3033" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20min-w-%5B60px%5D%22%7D" className="text-center min-w-[60px]">
            <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3031:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3031" data-component-end-line="3031" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-%5B%231D1D1F%5D%22%7D" className="text-3xl font-bold text-[#1D1D1F]">{quantity}</p>
            <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3032:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3032" data-component-end-line="3032" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%7D" className="text-xs text-[#86868B]">{unitNoun}</p>
          </div>
          <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3034:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3034" data-component-end-line="3043" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20rounded-full%20bg-%5B%23204653%5D%20text-white%20hover%3Abg-%5B%231a3a44%5D%20flex%20items-center%20justify-center%22%7D"
            type="button"
            onClick={() => onChange(clamp(quantity + 1))}
            className="w-10 h-10 rounded-full bg-[#204653] text-white hover:bg-[#1a3a44] flex items-center justify-center"
            aria-label="Increase"
          >
            <svg data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3040:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3040" data-component-end-line="3042" data-component-file="BookingFlowInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="16" height="16" viewBox="0 0 16 16">
              <path data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3041:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3041" data-component-end-line="3041" data-component-file="BookingFlowInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3046:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3046" data-component-end-line="3049" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-right%22%7D" className="text-right">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3047:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3047" data-component-end-line="3047" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Total%22%7D" className="text-xs text-[#86868B]">Total</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3048:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3048" data-component-end-line="3048" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-2xl font-bold text-[#1D1D1F]">₹{total.toLocaleString('en-IN')}</p>
        </div>
      </div>

      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3052:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3052" data-component-end-line="3073" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-gray-100%20mt-4%20pt-3%22%7D" className="border-t border-gray-100 mt-4 pt-3">
        <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3053:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3053" data-component-end-line="3053" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20mb-2%22%2C%22textContent%22%3A%22Quick%20select%3A%22%7D" className="text-xs text-[#86868B] mb-2">Quick select:</p>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3054:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3054" data-component-end-line="3072" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
          {quickRange.map((n) => {
            const selected = n === quantity;
            return (
              <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3058:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3058" data-component-end-line="3069" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22textContent%22%3A%22%C2%B7%20%E2%82%B9%22%7D"
                key={n}
                type="button"
                onClick={() => onChange(clamp(n))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  selected
                    ? 'bg-[#204653] text-white'
                    : 'bg-gray-100 text-[#1D1D1F] hover:bg-gray-200'
                }`}
              >
                {n} {chipNoun(n)} · ₹{(pricePerUnit * n).toLocaleString('en-IN')}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Area-based card for curtain / carpet — width × length × count + unit
// toggle (Feet / Meters). Mirrors admin manual booking's `AreaItemCard`.
const ExtraAreaCard = ({
  name,
  pricePerSqft,
  countLabel,
  width,
  length,
  count,
  unit,
  onChange,
}: {
  name: string;
  pricePerSqft: number;
  countLabel: string;
  width: string;
  length: string;
  count: string;
  unit: 'feet' | 'meters';
  onChange: (next: {
    width: string;
    length: string;
    count: string;
    unit: 'feet' | 'meters';
  }) => void;
}) => {
  const w = Number(width) || 0;
  const l = Number(length) || 0;
  const c = Number(count) || 0;
  const rawArea = w * l * c;
  const sqft = unit === 'meters' ? Math.round(rawArea * SQM_TO_SQFT) : Math.round(rawArea);
  const total = Math.round(sqft * pricePerSqft);

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3112:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3112" data-component-end-line="3191" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border%20border-gray-200%20bg-white%20p-5%22%7D" className="rounded-2xl border border-gray-200 bg-white p-5">
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3113:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3113" data-component-end-line="3126" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-4%20flex-wrap%20mb-4%22%7D" className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3114:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3114" data-component-end-line="3120" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3115:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3115" data-component-end-line="3115" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-bold%20text-%5B%231D1D1F%5D%22%7D" className="text-lg font-bold text-[#1D1D1F]">{name}</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3116:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3116" data-component-end-line="3119" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20font-semibold%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-base font-semibold text-[#1D1D1F] mt-2">
            ₹{pricePerSqft.toLocaleString('en-IN')}{' '}
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3118:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3118" data-component-end-line="3118" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22per%20sq.ft%22%7D" className="text-xs font-normal text-[#86868B]">per sq.ft</span>
          </p>
        </div>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3121:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3121" data-component-end-line="3125" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-right%22%7D" className="text-right">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3122:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3122" data-component-end-line="3122" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Area%20%C2%B7%20Total%22%7D" className="text-xs text-[#86868B]">Area · Total</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3123:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3123" data-component-end-line="3123" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22sq.ft%22%7D" className="text-sm font-semibold text-[#1D1D1F]">{sqft} sq.ft</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3124:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3124" data-component-end-line="3124" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20font-bold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-xl font-bold text-[#1D1D1F]">₹{total.toLocaleString('en-IN')}</p>
        </div>
      </div>

      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3128:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3128" data-component-end-line="3152" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-3%20bg-gray-50%20border%20border-gray-200%20rounded-lg%20px-3%20py-2%20w-fit%22%7D" className="flex items-center gap-2 mb-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-fit">
        <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3129:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3129" data-component-end-line="3129" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%20font-medium%22%2C%22textContent%22%3A%22Measurement%20Unit%3A%22%7D" className="text-xs text-[#86868B] font-medium">Measurement Unit:</span>
        <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3130:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3130" data-component-end-line="3140" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22textContent%22%3A%22Feet%22%7D"
          type="button"
          onClick={() => onChange({ width, length, count, unit: 'feet' })}
          className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
            unit === 'feet'
              ? 'bg-[#1D1D1F] text-white'
              : 'bg-white text-[#1D1D1F] border border-gray-300'
          }`}
        >
          Feet
        </button>
        <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3141:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3141" data-component-end-line="3151" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22textContent%22%3A%22Meters%22%7D"
          type="button"
          onClick={() => onChange({ width, length, count, unit: 'meters' })}
          className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
            unit === 'meters'
              ? 'bg-[#1D1D1F] text-white'
              : 'bg-white text-[#1D1D1F] border border-gray-300'
          }`}
        >
          Meters
        </button>
      </div>

      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3154:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3154" data-component-end-line="3190" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-3%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3155:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3155" data-component-end-line="3166" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3156:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3156" data-component-end-line="3156" data-component-file="BookingFlowInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22Width%20(%20)%20*%22%7D" className="block text-sm font-medium text-[#1D1D1F] mb-1">Width ({unit}) *</label>
          <input data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3157:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3157" data-component-end-line="3165" data-component-file="BookingFlowInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3Awidth%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20border%20border-gray-300%20rounded-lg%20bg-white%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%23204653%5D%20focus%3Aborder-%5B%23204653%5D%22%7D"
            type="number"
            min={0}
            step={0.1}
            value={width}
            onChange={(e) => onChange({ width: e.target.value, length, count, unit })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#204653] focus:border-[#204653]"
            placeholder="0"
          />
        </div>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3167:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3167" data-component-end-line="3178" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3168:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3168" data-component-end-line="3168" data-component-file="BookingFlowInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22Length%20(%20)%20*%22%7D" className="block text-sm font-medium text-[#1D1D1F] mb-1">Length ({unit}) *</label>
          <input data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3169:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3169" data-component-end-line="3177" data-component-file="BookingFlowInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3Alength%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20border%20border-gray-300%20rounded-lg%20bg-white%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%23204653%5D%20focus%3Aborder-%5B%23204653%5D%22%7D"
            type="number"
            min={0}
            step={0.1}
            value={length}
            onChange={(e) => onChange({ width, length: e.target.value, count, unit })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#204653] focus:border-[#204653]"
            placeholder="0"
          />
        </div>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3179:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3179" data-component-end-line="3189" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <label data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3180:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3180" data-component-end-line="3180" data-component-file="BookingFlowInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-%5B%231D1D1F%5D%20mb-1%22%2C%22textContent%22%3A%22*%22%7D" className="block text-sm font-medium text-[#1D1D1F] mb-1">{countLabel} *</label>
          <input data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3181:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3181" data-component-end-line="3188" data-component-file="BookingFlowInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3Acount%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20border%20border-gray-300%20rounded-lg%20bg-white%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%23204653%5D%20focus%3Aborder-%5B%23204653%5D%22%7D"
            type="number"
            min={1}
            value={count}
            onChange={(e) => onChange({ width, length, count: e.target.value, unit })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#204653] focus:border-[#204653]"
            placeholder="1"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Multi-size carpet card for the additional-service section. Wraps the
 * shared CarpetMultiSizeEditor so customers can add carpets of different
 * sizes as an extra service (same flow as the primary booking). The card
 * shell mirrors ExtraAreaCard's "name + price/sq.ft + line total" header
 * so it visually fits next to its curtain sibling.
 */
const ExtraCarpetCard = ({
  name,
  pricePerSqft,
  measurements,
  onChange,
}: {
  name: string;
  pricePerSqft: number;
  measurements: {
    carpetItems?: Array<{ label?: string; width: number; length: number; quantity: number }>;
    carpetWidth?: number;
    carpetLength?: number;
    numberOfCarpets?: number;
    measurementUnit?: 'feet' | 'meters';
  };
  onChange: (next: {
    carpetItems: Array<{ label?: string; width: number; length: number; quantity: number }>;
    measurementUnit: 'feet' | 'meters';
  }) => void;
}) => {
  const unit: 'feet' | 'meters' = measurements.measurementUnit === 'meters' ? 'meters' : 'feet';
  // Pass RAW carpetItems so partially-typed rows survive re-render — see
  // ServiceMeasurementStep for the same fix. Normalizer only hydrates the
  // very first render when the booking is still in the legacy shape.
  const items =
    measurements.carpetItems && measurements.carpetItems.length > 0
      ? measurements.carpetItems
      : normalizeCarpetItems(measurements);
  const totalCost = carpetLineCost(measurements, pricePerSqft) ?? 0;

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3233:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3233" data-component-end-line="3255" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border%20border-gray-200%20bg-white%20p-5%22%7D" className="rounded-2xl border border-gray-200 bg-white p-5">
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3234:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3234" data-component-end-line="3246" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-4%20flex-wrap%20mb-4%22%7D" className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3235:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3235" data-component-end-line="3241" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3236:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3236" data-component-end-line="3236" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-bold%20text-%5B%231D1D1F%5D%22%7D" className="text-lg font-bold text-[#1D1D1F]">{name}</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3237:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3237" data-component-end-line="3240" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20font-semibold%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-base font-semibold text-[#1D1D1F] mt-2">
            ₹{pricePerSqft.toLocaleString('en-IN')}{' '}
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3239:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3239" data-component-end-line="3239" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22per%20sq.ft%22%7D" className="text-xs font-normal text-[#86868B]">per sq.ft</span>
          </p>
        </div>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3242:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3242" data-component-end-line="3245" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-right%22%7D" className="text-right">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3243:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3243" data-component-end-line="3243" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Line%20total%22%7D" className="text-xs text-[#86868B]">Line total</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3244:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3244" data-component-end-line="3244" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20font-bold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-xl font-bold text-[#1D1D1F]">₹{totalCost.toLocaleString('en-IN')}</p>
        </div>
      </div>

      <CarpetMultiSizeEditor data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3248:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3248" data-component-end-line="3254" data-component-file="BookingFlowInteractive.tsx" data-component-name="CarpetMultiSizeEditor" data-component-content="%7B%22elementName%22%3A%22CarpetMultiSizeEditor%22%7D"
        items={items}
        unit={unit}
        pricePerSqft={pricePerSqft}
        onUnitChange={(nextUnit) => onChange({ carpetItems: items, measurementUnit: nextUnit })}
        onChange={(nextItems) => onChange({ carpetItems: nextItems, measurementUnit: unit })}
      />
    </div>
  );
};

/**
 * Multi-size curtain card for the additional-service section. Mirrors
 * ExtraCarpetCard exactly — same shell, same shared CarpetMultiSizeEditor
 * with kind="curtain" so labels read "Curtain 1 / Window note / Panels"
 * instead of carpet copy.
 */
const ExtraCurtainCard = ({
  name,
  pricePerSqft,
  measurements,
  onChange,
}: {
  name: string;
  pricePerSqft: number;
  measurements: {
    curtainItems?: Array<{ label?: string; width: number; length: number; quantity: number }>;
    curtainWidth?: number;
    curtainLength?: number;
    numberOfPanels?: number;
    measurementUnit?: 'feet' | 'meters';
  };
  onChange: (next: {
    curtainItems: Array<{ label?: string; width: number; length: number; quantity: number }>;
    measurementUnit: 'feet' | 'meters';
  }) => void;
}) => {
  const unit: 'feet' | 'meters' = measurements.measurementUnit === 'meters' ? 'meters' : 'feet';
  // Same bug-fix pattern as ExtraCarpetCard / ServiceMeasurementStep: pass
  // raw rows so partial entries don't get filtered between renders.
  const items =
    measurements.curtainItems && measurements.curtainItems.length > 0
      ? measurements.curtainItems
      : normalizeCurtainItems(measurements);
  const totalCost = curtainLineCost(measurements, pricePerSqft) ?? 0;

  return (
    <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3295:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3295" data-component-end-line="3318" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20border%20border-gray-200%20bg-white%20p-5%22%7D" className="rounded-2xl border border-gray-200 bg-white p-5">
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3296:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3296" data-component-end-line="3308" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-4%20flex-wrap%20mb-4%22%7D" className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3297:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3297" data-component-end-line="3303" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3298:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3298" data-component-end-line="3298" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-bold%20text-%5B%231D1D1F%5D%22%7D" className="text-lg font-bold text-[#1D1D1F]">{name}</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3299:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3299" data-component-end-line="3302" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20font-semibold%20text-%5B%231D1D1F%5D%20mt-2%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-base font-semibold text-[#1D1D1F] mt-2">
            ₹{pricePerSqft.toLocaleString('en-IN')}{' '}
            <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3301:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3301" data-component-end-line="3301" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-normal%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22per%20sq.ft%22%7D" className="text-xs font-normal text-[#86868B]">per sq.ft</span>
          </p>
        </div>
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3304:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3304" data-component-end-line="3307" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-right%22%7D" className="text-right">
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3305:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3305" data-component-end-line="3305" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Line%20total%22%7D" className="text-xs text-[#86868B]">Line total</p>
          <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3306:10" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3306" data-component-end-line="3306" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20font-bold%20text-%5B%231D1D1F%5D%22%2C%22textContent%22%3A%22%E2%82%B9%22%7D" className="text-xl font-bold text-[#1D1D1F]">₹{totalCost.toLocaleString('en-IN')}</p>
        </div>
      </div>

      <CarpetMultiSizeEditor data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3310:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3310" data-component-end-line="3317" data-component-file="BookingFlowInteractive.tsx" data-component-name="CarpetMultiSizeEditor" data-component-content="%7B%22elementName%22%3A%22CarpetMultiSizeEditor%22%7D"
        items={items}
        unit={unit}
        pricePerSqft={pricePerSqft}
        kind="curtain"
        onUnitChange={(nextUnit) => onChange({ curtainItems: items, measurementUnit: nextUnit })}
        onChange={(nextItems) => onChange({ curtainItems: nextItems, measurementUnit: unit })}
      />
    </div>
  );
};

/**
 * Per-service add-ons block for Step 2. Fetches the service's `addOns`
 * array from /api/services/:id and renders the same visual style as the
 * primary's add-on grid in ServiceSelectionStep. Toggling any card calls
 * back into the shared `selectedAddOns` list on the parent. Used once for
 * the primary service and once for every extra service the customer
 * stacked on Step 1, so a multi-service booking like Sofa + Full Bed shows
 * both services' add-ons side by side.
 */
const ServiceAddOnsBlock = ({
  packageId,
  serviceName,
  categoryType,
  selectedAddOns,
  onAddOnToggle,
  isPrimary = false,
}: {
  packageId: string;
  serviceName: string;
  categoryType: string;
  selectedAddOns: Array<string | { id: string; name: string; price: number }>;
  onAddOnToggle: (addOn: { id: string; name: string; price: number }) => void;
  isPrimary?: boolean;
}) => {
  const [addOns, setAddOns] = useState<
    Array<{ id: string; name: string; description: string; price: number; icon: string }>
  >([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!packageId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/services/${packageId}`);
        if (!res.ok) return;
        const svc = await res.json();
        if (cancelled) return;
        // Only surface add-ons that actually exist in this service's
        // catalogue. The previous version invented a "Stain Protection /
        // Deep Cleaning / Odor Removal" fallback when svc.addOns was empty
        // — but the server validates each add-on by name against
        // service.addOns, so any fabricated entry got rejected at submit
        // ("Extra service has items/add-ons not in its catalogue").
        const list =
          Array.isArray(svc?.addOns) && svc.addOns.length > 0
            ? svc.addOns.map((a: any, idx: number) => ({
                id: `${packageId}-addon-${idx}`,
                name: a.name,
                description: a.description || '',
                price: a.price,
                icon: a.icon || 'SparklesIcon',
              }))
            : [];
        setAddOns(list);
      } catch {
        /* leave list empty — section just shows the empty state */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageId]);

  // Nothing to render when this service's catalogue has no add-ons (e.g.
  // "Only Headboard"). Hiding the empty block keeps Step 2 focused on the
  // services that actually offer optional extras, and prevents the customer
  // from picking add-ons the server doesn't recognise. Curtains are no
  // longer special-cased — if the catalogue has add-ons we show them.
  if (loaded && addOns.length === 0) return null;

  return (
    <motion.div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3396:4" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3396" data-component-end-line="3469" data-component-file="BookingFlowInteractive.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3401:6" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3401" data-component-end-line="3414" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3402:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3402" data-component-end-line="3412" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-1%22%7D" className="flex items-center gap-2 mb-1">
          {isPrimary ? (
            <h2 data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3404:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3404" data-component-end-line="3406" data-component-file="BookingFlowInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20md%3Atext-3xl%20font-semibold%20text-%5B%231D1D1F%5D%20font-heading%20tracking-tight%22%2C%22textContent%22%3A%22Add-ons%20for%22%7D" className="text-2xl md:text-3xl font-semibold text-[#1D1D1F] font-heading tracking-tight">
              Add-ons for {serviceName || 'this service'}
            </h2>
          ) : (
            <h3 data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3408:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3408" data-component-end-line="3410" data-component-file="BookingFlowInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20md%3Atext-2xl%20font-semibold%20text-%5B%231D1D1F%5D%20font-heading%20tracking-tight%22%2C%22textContent%22%3A%22Add-ons%20for%22%7D" className="text-xl md:text-2xl font-semibold text-[#1D1D1F] font-heading tracking-tight">
              Add-ons for {serviceName || 'additional service'}
            </h3>
          )}
        </div>
        <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3413:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3413" data-component-end-line="3413" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%2386868B%5D%22%2C%22textContent%22%3A%22Enhance%20this%20service%20with%20optional%20add-ons%22%7D" className="text-[#86868B]">Enhance this service with optional add-ons</p>
      </div>

      {!loaded ? (
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3417:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3417" data-component-end-line="3421" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-5%20mt-6%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {[0, 1, 2].map((i) => (
            <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3419:12" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3419" data-component-end-line="3419" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20rounded-2xl%20bg-gray-100%20animate-pulse%22%7D" key={i} className="h-32 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3423:8" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3423" data-component-end-line="3467" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-5%20mt-6%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {addOns.map((addOn) => {
            const isSelected = selectedAddOns.some(
              (item) => (typeof item === 'string' ? item : item.id) === addOn.id
            );
            return (
              <button data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3429:14" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3429" data-component-end-line="3464" data-component-file="BookingFlowInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                key={addOn.id}
                type="button"
                onClick={() =>
                  onAddOnToggle({ id: addOn.id, name: addOn.name, price: addOn.price })
                }
                className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-left ${
                  isSelected
                    ? 'border-[#204653] bg-[#E4EDF0] shadow-[0_8px_30px_rgb(74,124,126,0.15)]'
                    : 'border-gray-100 hover:border-[#204653]/30 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]'
                }`}
              >
                {isSelected && (
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3442:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3442" data-component-end-line="3444" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-3%20right-3%20w-7%20h-7%20bg-%5B%23204653%5D%20rounded-full%20flex%20items-center%20justify-center%20shadow-md%22%7D" className="absolute top-3 right-3 w-7 h-7 bg-[#204653] rounded-full flex items-center justify-center shadow-md">
                    <Icon data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3443:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3443" data-component-end-line="3443" data-component-file="BookingFlowInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22text-white%22%7D" name="CheckIcon" size={14} className="text-white" />
                  </div>
                )}
                <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3446:16" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3446" data-component-end-line="3463" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3447:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3447" data-component-end-line="3453" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? 'bg-[#204653] text-white' : 'bg-[#F5F5F7] text-[#86868B]'
                    }`}
                  >
                    <Icon data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3452:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3452" data-component-end-line="3452" data-component-file="BookingFlowInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={addOn.icon as any} size={24} />
                  </div>
                  <div data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3454:18" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3454" data-component-end-line="3462" data-component-file="BookingFlowInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                    <h4 data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3455:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3455" data-component-end-line="3457" data-component-file="BookingFlowInteractive.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-semibold%20text-%5B%231D1D1F%5D%20font-heading%20mb-0.5%22%7D" className="font-semibold text-[#1D1D1F] font-heading mb-0.5">
                      {addOn.name}
                    </h4>
                    <p data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3458:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3458" data-component-end-line="3460" data-component-file="BookingFlowInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%2386868B%5D%20leading-relaxed%20mb-2%22%7D" className="text-sm text-[#86868B] leading-relaxed mb-2">
                      {addOn.description}
                    </p>
                    <span data-component-id="src/app/service-booking-flow/components/BookingFlowInteractive.tsx:3461:20" data-component-path="src/app/service-booking-flow/components/BookingFlowInteractive.tsx" data-component-line="3461" data-component-end-line="3461" data-component-file="BookingFlowInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-base%20font-bold%20text-%5B%23204653%5D%22%2C%22textContent%22%3A%22%2B%E2%82%B9%22%7D" className="text-base font-bold text-[#204653]">+₹{addOn.price}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};
